/*
  Background grain and video gating for snippets/background-media.liquid.

  Two jobs, both of them things CSS cannot do: generate a procedural texture,
  and stop a decoding video from burning battery off screen.

  Everything about how the texture *composites* — blend mode, opacity — lives in
  assets/background-media.css instead, so a merchant can dial the amount
  without a tile being rebuilt.

  The file is emitted once per background instance, and a page can carry two
  (home and store never coexist, but nothing stops a section adding one), so it
  guards against running twice and then finds every instance itself.
*/

(() => {
  if (window.bgMediaLoaded) return;
  window.bgMediaLoaded = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ====================================================================
     Grain
     ==================================================================== */

  const NOISE_TILE = 256;
  const FRAME_MS = 1000 / 24;

  /* Flicker grid, both measured off the reference it is named for: a speck
     every 4 CSS pixels, and a field that is replaced 20 times a second. The
     rate is the whole character — slow it down and the lattice settles into a
     screen door, speed it up and it dissolves into static. */
  const GRID_PITCH = 4;
  const GRID_STEP_MS = 50;

  /* Ordered dither threshold matrix. The classic 4x4 Bayer, which is the
     smallest pattern that still reads as a screen rather than a checker. */
  const BAYER_4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

  /* Central-limit approximation of a normal distribution. Real grain density
     falls off smoothly, and the tails do not matter at this scale. */
  function deviation() {
    return (Math.random() + Math.random() + Math.random() + Math.random() - 2) * 0.5;
  }

  function blankTile(size) {
    const tile = document.createElement('canvas');
    tile.width = size;
    tile.height = size;
    return tile;
  }

  /*
    Film grain. `cell` is the speck size in device pixels — one pixel is what
    makes it read as fine stock rather than a coarse digital pattern. `coarse`
    is a second, larger octave: silver halide clumps, so pure per-pixel noise
    looks like digital salt-and-pepper instead of grain. `spread` is peak alpha,
    and because specks fade toward the mean most pixels land far below it, so
    the image never hazes over into flat grey.
  */
  function noiseTile(cell, coarse, spread) {
    const tile = blankTile(NOISE_TILE);
    const ctx = tile.getContext('2d');
    const pixels = ctx.createImageData(NOISE_TILE, NOISE_TILE);
    const data = pixels.data;
    const fineWeight = 0.72;

    const span = NOISE_TILE / coarse;
    const clumps = new Float32Array(span * span);
    for (let i = 0; i < clumps.length; i++) clumps[i] = deviation();

    for (let y = 0; y < NOISE_TILE; y += cell) {
      const clumpRow = ((y / coarse) | 0) * span;
      for (let x = 0; x < NOISE_TILE; x += cell) {
        const g =
          deviation() * fineWeight +
          clumps[clumpRow + ((x / coarse) | 0)] * (1 - fineWeight);

        /* Light and dark specks with alpha from the deviation, so mid-tones
           stay clear instead of laying a grey veil over the background. */
        const value = g > 0 ? 255 : 0;
        const alpha = Math.min(255, Math.abs(g) * spread) | 0;

        for (let dy = 0; dy < cell && y + dy < NOISE_TILE; dy++) {
          for (let dx = 0; dx < cell && x + dx < NOISE_TILE; dx++) {
            const i = ((y + dy) * NOISE_TILE + (x + dx)) * 4;
            data[i] = value;
            data[i + 1] = value;
            data[i + 2] = value;
            data[i + 3] = alpha;
          }
        }
      }
    }

    ctx.putImageData(pixels, 0, 0);
    return tile;
  }

  /*
    Halftone dot screen, rotated 45 degrees by offsetting every second lattice
    point by half a pitch — the angle print uses because a square-on screen
    fights the horizontal and vertical edges in the image.

    Pitch is specified in CSS pixels and scaled to the device, so the screen
    looks the same size on a laptop and on a phone rather than getting four
    times finer. The dots are drawn antialiased: a hard-edged dot at this size
    aliases into a moire pattern as the layer scales.

    Worth being honest about the limit: a real halftone sizes each dot by the
    luminance underneath it, and a tiled pattern cannot see the image. This is a
    uniform screen laid over the frame, which reads as print without being it.
    Dot-size modulation is the first thing a fragment shader would buy.
  */
  function halftoneTile(dpr) {
    const pitch = Math.max(4, Math.round(5 * dpr));
    const size = pitch * 16;
    const tile = blankTile(size);
    const ctx = tile.getContext('2d');
    const radius = pitch * 0.24;

    /* The loops run to `size` inclusive, not exclusive. A dot centred on x = 0
       loses its left half to the canvas edge, and the only thing that can put
       that half back when the tile repeats is a dot centred on x = size. Stop
       one short and the pattern seams into a visible grid every tile. */
    ctx.fillStyle = 'rgba(0, 0, 0, 0.78)';
    for (let y = 0; y <= size; y += pitch) {
      for (let x = 0; x <= size; x += pitch) {
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
        /* The half-pitch offset row is what rotates the screen to 45 degrees.
           It never lands on an edge, so it needs no wrap. */
        ctx.beginPath();
        ctx.arc(x + pitch / 2, y + pitch / 2, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    return tile;
  }

  /*
    Ordered dither. The Bayer value becomes a signed offset around the middle of
    the matrix, so each cell gets either a dark or a light speck with an alpha
    set by how far from centre it sits. Composited with `overlay` that pushes
    the image apart in a fixed lattice, which is what gives a photocopy its
    crunch.

    Same honest limit as the halftone: true dithering thresholds against the
    image, and a tile cannot. Pairing this with the `photocopy` treatment is
    what sells it, because the crushed contrast leaves flat areas for the
    lattice to break up.
  */
  function ditherTile(dpr) {
    const cell = Math.max(1, Math.round(dpr));
    const size = cell * 4;
    const tile = blankTile(size);
    const ctx = tile.getContext('2d');
    const pixels = ctx.createImageData(size, size);
    const data = pixels.data;

    for (let my = 0; my < 4; my++) {
      for (let mx = 0; mx < 4; mx++) {
        const signed = (BAYER_4[my * 4 + mx] + 0.5) / 16 - 0.5;
        const value = signed > 0 ? 255 : 0;
        const alpha = Math.min(255, Math.abs(signed) * 2 * 255) | 0;

        for (let dy = 0; dy < cell; dy++) {
          for (let dx = 0; dx < cell; dx++) {
            const i = ((my * cell + dy) * size + (mx * cell + dx)) * 4;
            data[i] = value;
            data[i + 1] = value;
            data[i + 2] = value;
            data[i + 3] = alpha;
          }
        }
      }
    }

    ctx.putImageData(pixels, 0, 0);
    return tile;
  }

  /*
    Sparse luminance lattice: one speck of a single CSS pixel every
    GRID_PITCH, and nothing at all in between.

    The gaps are the effect. Every other stock here fills its tile, which is
    what makes noise read as emulsion; leaving 15 of every 16 cells clear
    leaves a ruled field of points instead, and the eye resolves the empty
    rows and columns as a grid. Each speck takes an unweighted random grey
    rather than a deviation around the mean, so a cell is as likely to be
    black as white and the field sparkles rather than hazes.

    Pitch and speck are measured in CSS pixels and scaled to the device, the
    same way the halftone screen is: a lattice that halved on a retina display
    would stop being legible as a grid. The tile is sized to a whole number of
    cells so the pattern cannot seam.
  */
  function flickerGridTile(dpr) {
    const pitch = Math.max(2, Math.round(GRID_PITCH * dpr));
    const speck = Math.max(1, Math.round(dpr));
    const size = pitch * 32;
    const tile = blankTile(size);
    const ctx = tile.getContext('2d');
    const pixels = ctx.createImageData(size, size);
    const data = pixels.data;

    for (let y = 0; y < size; y += pitch) {
      for (let x = 0; x < size; x += pitch) {
        const value = (Math.random() * 256) | 0;
        for (let dy = 0; dy < speck; dy++) {
          for (let dx = 0; dx < speck; dx++) {
            const i = ((y + dy) * size + (x + dx)) * 4;
            data[i] = value;
            data[i + 1] = value;
            data[i + 2] = value;
            data[i + 3] = 255;
          }
        }
      }
    }

    ctx.putImageData(pixels, 0, 0);
    return tile;
  }

  function makeTile(stock, dpr) {
    switch (stock) {
      case 'coarse':
        return noiseTile(2, 8, 155);
      case 'flicker-grid':
        return flickerGridTile(dpr);
      case 'halftone':
        return halftoneTile(dpr);
      case 'dither':
        return ditherTile(dpr);
      default:
        return noiseTile(1, 4, 190);
    }
  }

  function startGrain(canvas) {
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const stock = canvas.dataset.bgStock || 'fine';

    /* A print screen that jitters is not a print screen. Film moves only when
       asked; flicker is the grid stock's defining behaviour. */
    const canAnimate =
      stock === 'flicker-grid' ||
      (canvas.dataset.bgAnimate === 'true' && (stock === 'fine' || stock === 'coarse'));
    const frameMs = stock === 'flicker-grid' ? GRID_STEP_MS : FRAME_MS;

    let tiles = [];
    let patterns = [];
    let dpr = 0;
    let index = 0;
    let raf = 0;
    let last = 0;

    function currentDpr() {
      /* Matching device pixels is what keeps the grain fine on retina and 4K
         displays. Capped at 2 so the fill cost stays flat on denser screens. */
      return Math.min(window.devicePixelRatio || 1, 2);
    }

    function build() {
      dpr = currentDpr();
      const count = canAnimate ? 4 : 1;
      tiles = [];
      patterns = [];
      for (let i = 0; i < count; i++) tiles.push(makeTile(stock, dpr));
      patterns = tiles.map((tile) => ctx.createPattern(tile, 'repeat'));
      index = 0;
    }

    function resize() {
      const width = Math.max(1, Math.round((canvas.clientWidth || window.innerWidth) * dpr));
      const height = Math.max(1, Math.round((canvas.clientHeight || window.innerHeight) * dpr));
      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
    }

    function paint() {
      const pattern = patterns[index];
      if (!pattern) return;
      index = (index + 1) % patterns.length;

      const tile = tiles[0];
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      if (canAnimate && !reduced.matches) {
        ctx.translate((Math.random() * tile.width) | 0, (Math.random() * tile.height) | 0);
      }
      ctx.fillStyle = pattern;
      ctx.fillRect(-tile.width, -tile.height, canvas.width + tile.width * 2, canvas.height + tile.height * 2);
      ctx.restore();
    }

    function loop(now) {
      raf = requestAnimationFrame(loop);
      if (now - last < frameMs) return;
      last = now;
      paint();
    }

    function start() {
      if (!canAnimate || raf || reduced.matches || document.hidden) return;
      last = 0;
      raf = requestAnimationFrame(loop);
    }

    function stop() {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    }

    build();
    resize();
    paint();
    start();

    let resizeTimer = 0;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          /* Dragging a window to a different display changes the ratio, and the
             dot pitch of the print stocks is measured against it. */
          if (currentDpr() !== dpr) build();
          resize();
          paint();
        }, 150);
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });

    reduced.addEventListener('change', () => {
      if (reduced.matches) {
        stop();
        paint();
      } else {
        start();
      }
    });
  }

  /* ====================================================================
     Sequins

     One reference, and it is worth naming: the Larry Clark tee. A
     black-and-white photograph screened in vertical lines with a field of
     stacked sequins sewn over the top of it, and the whole surface changing
     colour as the garment moves. Nothing about it is complicated. That is
     the part to copy.

     SHAPE IS BAKED ONCE. LIGHT IS PAINTED EVERY FRAME.

     The tile holds the discs, their punched holes, their lit rims and the
     warp ruled through the lot — in alpha only, so it says how much light
     each pixel can return and says nothing at all about colour. Tiling is
     safe here in a way it is not for the print screens above, because
     sequins really are laid in a regular field. The repeat is only visible
     if the light repeats, and the light is viewport-scale and never does.

     So the discs never move and the light does. That is what a sequinned
     garment does, and it is the reason this reads as alive rather than as
     animated glitter — which is also why there is no per-disc twinkle here
     and should not be. Sequins sewn next to each other sit at the same
     angle and catch the same light; a field where every disc fires on its
     own timer is a screensaver.

     Two full-canvas fills a frame at 30fps, one more than the film stocks
     already pay for. Every arc happens once, at build.

     The ruling is load-bearing, not decoration. Unruled discs read as
     spilled glitter; the vertical line screen is what puts them on cloth,
     because it is the warp of the fabric and the halftone of the print at
     the same time, and it crosses the sequins as well as the gaps.
     ==================================================================== */

  /* The tile has to be big enough that its repeat is not readable at a
     glance, and the discs are large enough here that a 240px one was. */
  const SEQUIN_TILE = 400; /* CSS px */
  const SEQUIN_COLUMN = 13; /* CSS px between stitch lines */
  const SEQUIN_STEP = 11; /* CSS px down one — shorter than a disc, so they lap */
  const SEQUIN_RULE = 2; /* CSS px, the warp */
  const SEQUIN_FRAME_MS = 1000 / 30;
  const SEQUIN_STOPS = 16;
  const SEQUIN_SPAN = 1.1; /* how many holo cycles cross the frame */
  const ARC = Math.PI * 2;

  /*
    Silver holo, cycled through rather than hue-rotated. The macro shot of
    the print is a full oil slick, but at the size anyone actually sees the
    garment it is a white sparkle with the colour only showing at the edge of
    a highlight — so the anchors sit high and the hue is a lean, not a
    rainbow. A plain HSL sweep gives you a unicorn decal.
  */
  const HOLO = [
    [126, 142, 186],
    [190, 212, 236],
    [255, 255, 255],
    [240, 226, 196],
    [182, 164, 206],
  ];

  function holoAt(p, out) {
    const n = HOLO.length;
    const s = (p - Math.floor(p)) * n;
    const i = s | 0;
    const f = s - i;
    const t = f * f * (3 - 2 * f);
    const a = HOLO[i];
    const b = HOLO[(i + 1) % n];
    out[0] = a[0] + (b[0] - a[0]) * t;
    out[1] = a[1] + (b[1] - a[1]) * t;
    out[2] = a[2] + (b[2] - a[2]) * t;
  }

  /*
    One disc: a flat face and a lit edge. The rim is what stops it reading as
    a dot. The punch is what stops it reading as a bead, and it cuts through
    whatever is beneath it too, because it is a gap in the field rather than
    a mark on one sequin.
  */
  function disc(ctx, x, y, r, face) {
    ctx.fillStyle = `rgba(255,255,255,${face * 0.82})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, ARC);
    ctx.fill();

    ctx.strokeStyle = `rgba(255,255,255,${face})`;
    ctx.lineWidth = Math.max(1, r * 0.18);
    ctx.beginPath();
    ctx.arc(x, y, r * 0.9, 0, ARC);
    ctx.stroke();

    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(x, y, r * 0.17, 0, ARC);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }

  /*
    Columns, not a scatter — and this is the one thing that decides whether the
    result looks like the garment or like spilled glitter.

    Sequins are not sprinkled on. They are stitched down vertical lines, which
    is why the reference has both a visible column structure and a visible
    warp: those are the same fact seen twice. So each column is pinned to a
    thread and its discs are stepped down it at less than a disc apart, which
    makes them lap like roof tiles. Adjacent columns start at their own
    offset, or every row would land in a band.

    The exceptions matter as much as the rule. Roughly one disc in twelve has
    slipped off its thread and one cell in ten is bare, because a field with no
    outliers reads as a screen print of sequins rather than as sequins.
  */
  function sequinTile(dpr) {
    const rule = Math.max(2, Math.round(SEQUIN_RULE * dpr));
    const thread = Math.max(1, Math.round(dpr));
    /* Snapped to a whole number of threads, or the warp seams at every tile
       edge — the same trap the halftone screen documents above. */
    const size = Math.max(1, Math.round((SEQUIN_TILE * dpr) / rule)) * rule;
    const tile = blankTile(size);
    const ctx = tile.getContext('2d');
    const columns = Math.round(SEQUIN_TILE / SEQUIN_COLUMN);
    const columnPitch = size / columns;
    const steps = Math.round(SEQUIN_TILE / SEQUIN_STEP);
    const stepPitch = size / steps;

    for (let c = 0; c < columns; c++) {
      /* Landed on a thread rather than near one. The stitch line and the warp
         line are the same line. */
      const stitch = Math.round((c * columnPitch) / rule) * rule;
      const offset = Math.random();
      for (let i = 0; i < steps; i++) {
        if (Math.random() > 0.9) continue;
        const r = (4.4 + Math.random() * 3.4) * dpr;
        const stray = Math.random() < 0.08 ? (Math.random() - 0.5) * columnPitch : 0;
        const x = stitch + (Math.random() - 0.5) * 1.6 * dpr + stray;
        /* Whole steps plus the column's own offset, so the run closes on the
           tile edge exactly and the column can repeat without a join. */
        const y = (i + offset + (Math.random() - 0.5) * 0.3) * stepPitch;
        const face = 0.32 + Math.random() * 0.68;
        /* Drawn again on the far side wherever it overhangs, so the tile
           carries its own overlap and cannot seam. */
        for (let wy = -1; wy <= 1; wy++) {
          for (let wx = -1; wx <= 1; wx++) {
            const px = x + wx * size;
            const py = y + wy * size;
            if (px < -r || px > size + r || py < -r || py > size + r) continue;
            disc(ctx, px, py, r, face);
          }
        }
      }
    }

    /* The cloth between the discs. Barely there — just enough that the gaps
       catch a little of the same light instead of going dead. */
    ctx.fillStyle = 'rgba(255,255,255,0.09)';
    for (let x = 0; x < size; x += rule) ctx.fillRect(x, 0, thread, size);

    /* And the grooves, cut through discs and cloth alike, half a pitch over
       so they fall between the threads above rather than on top of them. */
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0,0,0,0.42)';
    for (let x = 0; x < size; x += rule) ctx.fillRect(x + (rule >> 1), 0, thread, size);
    ctx.globalCompositeOperation = 'source-over';

    return tile;
  }

  function startSequins(canvas) {
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const rgb = [0, 0, 0];
    let dpr = 0;
    let mask = null;
    let raf = 0;
    let last = 0;
    /* Accumulated rather than read off the clock, so coming back to a
       backgrounded tab does not jump the light across the frame. */
    let clock = 0;

    /*
      Turning the cloth toward the light. `pointed` and `scrolled` are what is
      being asked for and `turn` chases them, because snapping the light to
      the cursor reads as a bug and gliding to it reads as a hand.
    */
    let pointed = 0;
    let scrolled = 0;
    let turn = 0;

    function currentDpr() {
      return Math.min(window.devicePixelRatio || 1, 2);
    }

    function build() {
      dpr = currentDpr();
      mask = ctx.createPattern(sequinTile(dpr), 'repeat');
    }

    function resize() {
      const width = Math.max(1, Math.round((canvas.clientWidth || window.innerWidth) * dpr));
      const height = Math.max(1, Math.round((canvas.clientHeight || window.innerHeight) * dpr));
      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
    }

    function paint() {
      const w = canvas.width;
      const h = canvas.height;
      if (!mask || !w || !h) return;

      /* One ramp across the frame, long enough to clear both corners. Its
         angle swings and its colours slide at rates that do not divide into
         each other, so the light never lands back where it was. */
      const angle = 1.15 + turn + Math.sin(clock * 0.043) * 0.22;
      const reach = Math.hypot(w, h) * 0.6;
      const dx = Math.cos(angle) * reach;
      const dy = Math.sin(angle) * reach;
      const ramp = ctx.createLinearGradient(w / 2 - dx, h / 2 - dy, w / 2 + dx, h / 2 + dy);
      const phase = clock * 0.03 + turn * 0.5;

      for (let i = 0; i < SEQUIN_STOPS; i++) {
        const s = i / (SEQUIN_STOPS - 1);
        holoAt(s * SEQUIN_SPAN + phase, rgb);
        ramp.addColorStop(s, `rgb(${rgb[0] | 0} ${rgb[1] | 0} ${rgb[2] | 0})`);
      }

      /* `copy` rather than clear-then-fill: the ramp covers every pixel, so
         the clear is a full-canvas write nobody reads. */
      ctx.globalCompositeOperation = 'copy';
      ctx.fillStyle = ramp;
      ctx.fillRect(0, 0, w, h);

      /* Cut the light to the shape of the sequins. */
      ctx.globalCompositeOperation = 'destination-in';
      ctx.fillStyle = mask;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';
    }

    function loop(now) {
      raf = requestAnimationFrame(loop);
      if (!last) {
        last = now;
        return;
      }
      const dt = now - last;
      if (dt < SEQUIN_FRAME_MS) return;
      last = now;
      /* Clamped, so a long stall moves the light on by a frame rather than by
         however long the tab was busy. */
      clock += Math.min(dt, 100) / 1000;
      turn += (pointed + scrolled - turn) * 0.06;
      paint();
    }

    function start() {
      if (raf || reduced.matches || document.hidden) return;
      last = 0;
      raf = requestAnimationFrame(loop);
    }

    function stop() {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    }

    build();
    resize();
    paint();
    start();

    let resizeTimer = 0;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (currentDpr() !== dpr) build();
          resize();
          paint();
        }, 150);
      },
      { passive: true }
    );

    /* A touch device has no pointer to turn the cloth with, so there the
       scroll does the whole job — which is the truer hook anyway, since a
       thumb dragging the page is the closest thing to a hand moving a shirt. */
    if (window.matchMedia('(hover: hover)').matches) {
      window.addEventListener(
        'pointermove',
        (event) => {
          pointed = event.clientX / window.innerWidth - 0.5;
        },
        { passive: true }
      );
    }

    window.addEventListener(
      'scroll',
      () => {
        scrolled = ((window.scrollY || 0) / Math.max(1, window.innerHeight)) * 0.5;
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });

    reduced.addEventListener('change', () => {
      if (reduced.matches) stop();
      else start();
    });
  }

  /* ====================================================================
     Video
     ==================================================================== */

  function gateVideo(video) {
    const shouldHold = () => reduced.matches || document.hidden;

    const sync = () => {
      if (shouldHold()) {
        video.pause();
        return;
      }
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    };

    /* The autoplay attribute can fire after this runs, so re-check on play
       rather than trusting a single pause up front. */
    video.addEventListener('play', () => {
      if (shouldHold()) video.pause();
    });

    /* Decoding an offscreen background video burns battery for no benefit. */
    document.addEventListener('visibilitychange', sync);
    reduced.addEventListener('change', sync);

    sync();
  }

  /* ====================================================================
     Quantise

     The destructive axis. Grain and treatment are additive — they lay
     something over an image that still holds all of its tone, which is why a
     heavily treated render can still read as rendered. These modes throw tone
     or resolution away instead, and what is left is texture.

     Every mode is the same three steps: draw the source into a small buffer,
     rewrite the buffer, blow it back up to the canvas. The buffer size is the
     effect. Working small is not an optimisation here, it is the point.
     ==================================================================== */

  /* Sparse to dense. Space first so an empty cell is genuinely empty. */
  const GLYPHS = ' .:-=+*#%@';

  /* Limited-palette pixelate. Six is the count the reference reads as: enough
     for a light, a dark, a mid and three hues, few enough that each cell is a
     decision rather than a sample. The radius is a squared colour distance and
     sets how wide a colour's neighbourhood is; the refit pair governs when an
     animation may replace its palette. Both are explained where they are used,
     in extractPixelPalette() and pixelate(). */
  const PIXEL_PALETTE_SIZE = 6;
  const PALETTE_MODE_RADIUS = 56 * 56;
  const PALETTE_REFIT_MARGIN = 1.6;
  const PALETTE_REFIT_MS = 700;

  /*
    8x8 ordered dither. Larger than the 4x4 used for grain because this one is
    quantising real tone rather than adding a lattice, and 64 thresholds put
    the banding below the point where the eye reads it as a pattern.

    Bit significance runs high to low, and that ordering is the whole matrix.
    Interleaving the same (x^y, y) bit pairs from the bottom up produces a
    permutation that still looks like a threshold map and still dithers, but
    correlates along rows — it comes out as horizontal streaking rather than
    the even cross-hatch a Bayer matrix is chosen for.
  */
  const BAYER_8 = (() => {
    const m = new Uint8Array(64);
    for (let y = 0; y < 8; y++) {
      for (let x = 0; x < 8; x++) {
        let v = 0;
        for (let i = 2; i >= 0; i--) {
          const bx = (x >> i) & 1;
          const by = (y >> i) & 1;
          v = (v << 2) | ((bx ^ by) << 1) | by;
        }
        m[y * 8 + x] = v;
      }
    }
    return m;
  })();

  /*
    8x8 clustered-dot screen, for print rather than for dithering.

    The distinction matters and it is the reason there are two matrices here.
    Bayer *disperses*: it scatters its thresholds so that ink lands as far from
    other ink as possible, which is what you want when the goal is to fake a
    missing colour, and it is why half coverage comes out as a perfect
    checkerboard. A press cannot do that — ink spreads, so a press *clusters*,
    growing one solid dot per cell outward from the centre until the cells
    touch. Half coverage then reads as grey and the image survives it.

    Thresholds are ranked by a round spot function, highest value first, so the
    lowest threshold sits at the middle of each cell.
  */
  const CLUSTER_8 = (() => {
    const n = 8;
    const cells = [];
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        cells.push([
          x,
          y,
          -Math.cos((2 * Math.PI * (x + 0.5)) / n) -
            Math.cos((2 * Math.PI * (y + 0.5)) / n),
        ]);
      }
    }
    cells.sort((a, b) => b[2] - a[2]);
    const m = new Uint8Array(n * n);
    cells.forEach((cell, rank) => {
      m[cell[1] * n + cell[0]] = rank;
    });
    return m;
  })();

  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp255 = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);
  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const luma = (r, g, b) => r * 0.2126 + g * 0.7152 + b * 0.0722;

  function parseHex(hex, fallback) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return fallback;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  /*
    Destination rectangle reproducing `object-fit: cover`, so the quantised
    canvas frames the image exactly as the <img> it covers would have: the
    whole image scaled to fill the buffer, the overflow clipped by the buffer's
    own edges.

    Deliberately a destination rectangle and not a source crop, which is the
    obvious way to write this and is wrong. `naturalWidth` on a srcset image is
    density-corrected: the background GIF here is a 400px bitmap that reports
    1767px because that is the slot it was picked for. drawImage's *source*
    rectangle is measured against the real bitmap, so asking for a 1767-wide
    crop of a 400-wide bitmap draws only the sliver that intersects and leaves
    the rest of the buffer transparent — no error, just a black canvas.

    Only the ratio is needed here, and the ratio is what survives the density
    correction, because it scales both axes by the same factor.

    `stretch` compensates for a buffer whose cells are not square, which is the
    text mode: its grid is one entry per character cell, and a character cell
    is taller than it is wide.
  */
  function coverDest(ar, cw, ch, stretch) {
    const target = ar * (stretch || 1);
    let w = cw;
    let h = cw / target;
    if (h < ch) {
      h = ch;
      w = ch * target;
    }
    return [(cw - w) / 2, (ch - h) / 2, w, h];
  }

  /* ====================================================================
     Quantise on the GPU

     Color, print and glyph passes stay on the GPU to avoid a full-image
     readback and thousands of JavaScript pixel operations per frame. Tone is
     sampled at each format's own resolution, then evaluated in one draw (plus
     a filtered enlargement for the smooth formats). The 2D paths below retain
     the same transfer functions and serve devices without WebGL.
     ==================================================================== */

  /* Three vertices covering the viewport, derived from the vertex index, so
     nothing here needs a buffer or an attribute. */
  const QUAD_VS = `#version 300 es
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

  const LUMA_GLSL = 'const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);';

  // The response texture is a sparse field of independently recovering cells.
  // Filtering joins neighbouring charges without translating the source image.
  const RESPONSE_GLSL = `
uniform sampler2D uResponse;
float responseAt(vec2 uv) {
  float value = (texture(uResponse, uv).r * 255.0 - 128.0) / 127.0;
  return abs(value) < 0.008 ? 0.0 : value;
}`;

  const COLOR_FS = `#version 300 es
precision highp float;
uniform sampler2D uTone;
uniform vec2 uGrid;
uniform vec2 uOutput;
uniform vec2 uCanvas;
uniform float uMode;
uniform float uSteps;
uniform float uContrast;
uniform float uDrift;
uniform vec3 uInk;
uniform vec3 uInkB;
uniform vec3 uPaper;
out vec4 fragColor;
${LUMA_GLSL}
${RESPONSE_GLSL}
const int BAYER[64] = int[64](${BAYER_8.join(',')});
const int CLUSTER[64] = int[64](${Array.from(CLUSTER_8).join(',')});
float plateAt(vec2 uv, int plate) {
  vec2 cell = clamp(floor(uv * uGrid), vec2(0.0), uGrid - 1.0);
  float light = dot(texture(uTone, (cell + 0.5) / uGrid).rgb, LUMA);
  float coverage = plate == 0 ? (1.0 - light) * 0.9 : (1.0 - abs(light - 0.45) * 2.2) * 0.75;
  ivec2 screen = (ivec2(cell) + ivec2(plate * 4)) & 7;
  float threshold = (float(CLUSTER[screen.y * 8 + screen.x]) + 0.5) / 64.0;
  return coverage > threshold ? 1.0 : 0.0;
}
void main() {
  vec2 uv = vec2(gl_FragCoord.x, uOutput.y - gl_FragCoord.y) / uOutput;
  float charge = responseAt(uv);
  if (uMode == 3.0) {
    float pad = uDrift * 2.0;
    vec2 px = uv * uCanvas;
    vec2 size = uCanvas + pad * 2.0;
    vec2 a = (px - vec2(-uDrift, -floor(uDrift * 0.4 + 0.5)) + pad) / size;
    vec2 b = (px - vec2(floor(uDrift * 0.8 + 0.5), uDrift) + pad) / size;
    // Only the second plate slips locally; the paper and first ink stay put.
    b += vec2(charge, -charge * 0.6) * (uDrift + 2.0) / size;
    vec3 ink = uPaper * mix(vec3(1.0), uInk, plateAt(a, 0)) * mix(vec3(1.0), uInkB, plateAt(b, 1));
    fragColor = vec4(ink, 1.0);
    return;
  }
  vec2 cell = clamp(floor(uv * uGrid), vec2(0.0), uGrid - 1.0);
  vec4 src = texture(uTone, (cell + 0.5) / uGrid);
  vec3 color;
  if (uMode == 2.0) {
    // Match the CPU's 8-bit luminance LUT, including its truncation.
    float light = floor(dot(src.rgb * 255.0, LUMA) + 0.0001) / 255.0;
    float tone = clamp((light - 0.5) * uContrast + 0.5 + charge * 0.2, 0.0, 1.0);
    color = floor(mix(uInk, uInkB, tone) * 255.0 + 0.0001) / 255.0;
  } else {
    float threshold = 0.0;
    if (uMode == 1.0) {
      ivec2 screen = ivec2(cell) & 7;
      threshold = (float(BAYER[screen.y * 8 + screen.x]) + 0.5) / 64.0 - 0.5;
    }
    // Shift a local threshold, keeping the band count / dither lattice intact.
    color = clamp(floor(src.rgb * uSteps + threshold + charge * 0.6 + 0.5) / uSteps, 0.0, 1.0);
  }
  fragColor = vec4(color * src.a, src.a);
}`;

  /*
    Engrave, transcribed from engrave() below — the comments on the original
    explain every constant in here.

    `yi` converts gl_FragCoord's bottom-up y into the top-down row index the 2D
    pass counts in. The screen's two axes are defined against that index, so a
    flipped y would mirror the strokes.
  */
  const ENGRAVE_FS = `#version 300 es
precision highp float;
uniform sampler2D uTone;
uniform vec2 uSize;
uniform vec4 uScreen;
uniform float uContrast;
uniform float uInvert;
uniform vec3 uInk;
uniform vec3 uPaper;
out vec4 fragColor;
${LUMA_GLSL}
${RESPONSE_GLSL}
void main() {
  float xi = floor(gl_FragCoord.x);
  float yi = floor(uSize.y - gl_FragCoord.y);
  float l = dot(texture(uTone, (vec2(xi, yi) + 0.5) / uSize).rgb, LUMA);
  float tone = mix(l, 1.0 - l, uInvert);
  float cov = clamp((tone - 0.5) * uContrast + 0.5, 0.0, 1.0);
  float charge = responseAt((vec2(xi, yi) + 0.5) / uSize);
  float p0 = xi * uScreen.x + yi * uScreen.y + charge * 0.28;
  float p1 = xi * uScreen.z + yi * uScreen.w - charge * 0.18;
  float d0 = abs(p0 - floor(p0) - 0.5) * 2.0;
  float d1 = abs(p1 - floor(p1) - 0.5) * 2.0;
  bool inked = d0 < cov * 0.95 || d1 < clamp((cov - 0.5) / 0.5, 0.0, 1.0) * 0.35;
  fragColor = vec4(inked ? uInk : uPaper, 1.0);
}`;

  /* The blow-up, and it is a separate pass for the same reason blowUp(true)
     exists: the strokes are thresholded in the small buffer and then resampled
     soft, because a one-pixel stroke scaled hard aliases into a moire that
     swamps the screen it came from. */
  const BLIT_FS = `#version 300 es
precision highp float;
uniform sampler2D uSrc;
uniform vec2 uCanvas;
out vec4 fragColor;
void main() {
  fragColor = texture(uSrc, gl_FragCoord.xy / uCanvas);
}`;

  /*
    Readout, transcribed from readout() below.

    The 2D pass hoists a luminance array so the detail test does not recompute
    each cell nine times over. Here the nine reads are nine texture fetches out
    of the same cache line, which is cheaper than the array was, so the tone
    buffer is read directly and the hoist is not needed.
  */
  const READOUT_FS = `#version 300 es
precision highp float;
uniform sampler2D uTone;
uniform sampler2D uAtlas;
uniform vec2 uGrid;
uniform vec2 uCell;
uniform vec2 uCanvas;
uniform vec2 uAtlasSize;
uniform float uGlyphs;
uniform float uFloor;
uniform float uSpan;
uniform float uOverlay;
uniform float uSoftAscii;
uniform float uInvert;
uniform vec3 uPaper;
out vec4 fragColor;
${LUMA_GLSL}
${RESPONSE_GLSL}
float toneAt(vec2 cell) {
  vec2 q = clamp(cell, vec2(0.0), uGrid - 1.0);
  return dot(texture(uTone, (q + 0.5) / uGrid).rgb, LUMA);
}
void main() {
  vec2 px = vec2(gl_FragCoord.x, uCanvas.y - gl_FragCoord.y);
  vec2 cell = floor(px / uCell);
  float charge = responseAt((cell + 0.5) / uGrid);
  if (uOverlay < 0.5) {
    vec3 rgb = texture(uTone, (cell + 0.5) / uGrid).rgb * 255.0;
    float light = dot(rgb, LUMA) / 255.0;
    float coverage = clamp(mix(light, 1.0 - light, uInvert) + charge * 0.18, 0.0, 1.0);
    if (uSoftAscii > 0.5) coverage = clamp((coverage - 0.08) / 0.92, 0.0, 1.0);
    float gi = min(uGlyphs - 1.0, floor(coverage * uGlyphs));
    if (uSoftAscii > 0.5) {
      vec4 glyph = texture(uAtlas, (vec2(gi, 26.0) + fract(px / uCell)) * uCell / uAtlasSize);
      vec3 ink = rgb / 255.0 * mix(1.0, 0.45, uInvert);
      fragColor = vec4(mix(uPaper, ink, glyph.a), 1.0);
      return;
    }
    float target = mix(255.0, 127.0, uInvert);
    float peak = max(rgb.r, max(rgb.g, rgb.b));
    vec3 band = floor(rgb * target / max(peak, 1.0) / 96.0);
    float ci = gi == 0.0 ? 0.0 : dot(band, vec3(9.0, 3.0, 1.0));
    vec4 g = texture(uAtlas, (vec2(gi, ci) + fract(px / uCell)) * uCell / uAtlasSize);
    fragColor = vec4(mix(uPaper, g.rgb, g.a), 1.0);
    return;
  }
  float lo = 1.0;
  float hi = 0.0;
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      float v = toneAt(cell + vec2(float(dx), float(dy)));
      lo = min(lo, v);
      hi = max(hi, v);
    }
  }
  float range = hi - lo + abs(charge) * 0.16;
  if (range < uFloor) {
    fragColor = vec4(0.0);
    return;
  }
  float t = clamp((range - uFloor) / uSpan, 0.0, 1.0);
  float gi = min(uGlyphs - 1.0, 1.0 + floor(t * (uGlyphs - 1.0)));
  float ci = toneAt(cell) > 0.62 ? 0.0 : 26.0;
  vec4 g = texture(uAtlas, (vec2(gi, ci) + fract(px / uCell)) * uCell / uAtlasSize);
  fragColor = vec4(g.rgb * g.a, g.a);
}`;

  const HALFTONE_FLICKER_HZ = 3;

  /* Fixed bulbs, discrete relay states. Pointer movement seeds brief faults
     into specific grid cells. Their lifetimes are shared by both renderers;
     no shader mask follows the cursor or keeps firing while it rests. */
  const HALFTONE_VS = `#version 300 es
precision highp float;
uniform sampler2D uTone;
uniform vec2 uCanvas;
uniform vec2 uGrid;
uniform float uPitch;
uniform float uTime;
uniform float uMotion;
uniform sampler2D uFaults;
uniform float uColors;
uniform vec3 uInk;
uniform vec3 uInkB;
uniform vec3 uPaper;
out vec3 vColor;
out float vRadius;
out float vSize;
out float vLevel;
${LUMA_GLSL}
float seed(vec2 cell) {
  uint n = uint(cell.x) * 374761393u + uint(cell.y) * 668265263u;
  n = (n ^ (n >> 13u)) * 1274126177u;
  return float(n ^ (n >> 16u)) / 4294967295.0;
}

void main() {
  vec2 cell = vec2(mod(float(gl_VertexID), uGrid.x), floor(float(gl_VertexID) / uGrid.x));
  vec2 center = (cell + 0.5) * uPitch;
  vec4 src = texture(uTone, (cell + 0.5) / uGrid);
  float light = dot(src.rgb, LUMA);
  float contrast = clamp(length(src.rgb - uPaper) / 1.15, 0.0, 1.0);
  float tone = mix(contrast, light, uColors == 1.0 ? 0.0 : 0.72);
  float random = seed(cell);
  float density = smoothstep(0.12, 0.95, tone);
  float on = step(random, density * 0.78 + 0.08);
  float weak = seed(cell + vec2(53.0, 97.0)) > 0.96 ? 0.45 : 1.0;
  float tripTick = floor(uTime * ${HALFTONE_FLICKER_HZ.toFixed(1)} + random * 19.0);
  float trip = seed(cell + vec2(tripTick * 17.0 + 137.0, tripTick * 29.0 + 277.0));
  float level = on * weak * (trip < uMotion * 0.003 ? 0.25 : 1.0);
  float faultState = floor(texture(uFaults, (cell + 0.5) / uGrid).r * 255.0 + 0.5);
  float faultLevel = faultState < 1.5 ? 0.0 : faultState < 2.5 ? 0.4 : 1.0;
  vec3 color = src.rgb;
  if (uColors == 0.0) {
    color = vec3(0.2902, 0.0706, 0.9216);
    color = mix(color, vec3(0.8471, 0.2706, 0.7725), smoothstep(0.17, 0.42, light));
    float warm = smoothstep(0.05, 0.35, src.r - src.b) * (1.0 - smoothstep(0.5, 0.8, light));
    color = mix(color, vec3(0.9216, 0.2706, 0.2314), warm * 0.9);
    color = mix(color, vec3(0.6, 0.9725, 0.9765), smoothstep(0.42, 0.77, light));
  } else if (uColors == 2.0) {
    color = mix(uInk, uInkB, smoothstep(0.15, 0.85, light));
  }
  float charge = faultState == 3.0 ? 0.78 : faultState == 4.0 ? 0.28 : 0.0;
  vColor = mix(color, vec3(1.0), charge);
  vLevel = (faultState > 0.0 ? faultLevel : level) * src.a;
  vRadius = 0.4 * uPitch;
  vSize = max(1.0, ceil(vRadius * 2.0 + 2.0));
  gl_PointSize = vSize;
  gl_Position = vec4(center.x / uCanvas.x * 2.0 - 1.0, 1.0 - center.y / uCanvas.y * 2.0, 0.0, 1.0);
}`;

  const HALFTONE_FS = `#version 300 es
precision highp float;
in vec3 vColor;
in float vRadius;
in float vSize;
in float vLevel;
out vec4 fragColor;
void main() {
  float distanceToDot = length(gl_PointCoord - 0.5) * vSize;
  float ink = (1.0 - smoothstep(max(0.0, vRadius - 0.8), vRadius + 0.8, distanceToDot)) * step(0.001, vRadius);
  ink *= vLevel;
  fragColor = vec4(vColor * ink, ink);
}`;

  // Narrow, individually lit tiles. The grid is measured in CSS pixels; the
  // source is averaged per tile before shading, so video does not shimmer.
  // Bloom remains a separate layer above this pass.
  const MOSAIC_FS = `#version 300 es
precision highp float;
uniform sampler2D uTone;
uniform vec2 uGrid;
uniform vec2 uCanvas;
uniform float uAmount;
uniform float uSeam;
uniform sampler2D uGlints;
out vec4 fragColor;
${LUMA_GLSL}
void main() {
  vec2 pixel = vec2(gl_FragCoord.x, uCanvas.y - gl_FragCoord.y);
  vec2 cellSize = uCanvas / uGrid;
  vec2 cell = floor(pixel / cellSize);
  vec4 source = texture(uTone, (cell + 0.5) / uGrid);
  float light = dot(source.rgb, LUMA);
  vec3 color = mix(vec3(light), source.rgb, 1.0 + 0.6 * uAmount);
  color = clamp((color - 0.5) * (1.0 + 0.8 * uAmount) + 0.5, 0.0, 1.0);
  float variation = mod(cell.x * 17.0 + cell.y * 131.0, 101.0) / 100.0 - 0.5;
  color = clamp(color * (1.0 + variation * 0.18), 0.0, 1.0);
  float steps = floor(mix(48.0, 8.0, uAmount) + 0.5);
  color = floor(color * steps + 0.5) / steps;
  vec2 local = mod(pixel, cellSize);
  vec2 edge = min(local, cellSize - local);
  float face = smoothstep(uSeam * 0.2, uSeam * 0.8, min(edge.x, edge.y));
  // Dark joints carry the grid even through pale highlights; no RGB stripes.
  vec3 shaded = color * mix(0.3, 1.0, face);
  vec4 glint = texture(uGlints, (cell + 0.5) / uGrid);
  vec2 direction = normalize(glint.ba * 2.0 - 1.0);
  float projection = dot(local / cellSize - 0.5, direction);
  float sweep = mix(-0.8, 0.8, glint.g);
  float band = exp(-pow((projection - sweep) / 0.24, 2.0));
  // Reflect existing colour. Empty black cells never become stray white dots.
  float reflectance = smoothstep(0.015, 0.5, max(color.r, max(color.g, color.b)));
  float lightCatch = glint.r * reflectance * (0.08 + 0.32 * band);
  shaded = mix(shaded, vec3(1.0), lightCatch);
  fragColor = vec4(shaded * source.a, source.a);
}`;

  const GPU_FRAGMENT = {
    mosaic: MOSAIC_FS,
    posterise: COLOR_FS, dither: COLOR_FS, duotone: COLOR_FS, riso: COLOR_FS,
    engrave: ENGRAVE_FS, ascii: READOUT_FS, 'ascii-soft': READOUT_FS, readout: READOUT_FS, halftone: HALFTONE_FS,
  };

  function compileShader(gl, type, src) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
    gl.deleteShader(shader);
    return null;
  }

  function linkProgram(gl, fragment, vertex = QUAD_VS) {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vertex);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragment);
    if (!vs || !fs) {
      if (vs) gl.deleteShader(vs);
      if (fs) gl.deleteShader(fs);
      return null;
    }
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (gl.getProgramParameter(program, gl.LINK_STATUS)) return program;
    gl.deleteProgram(program);
    return null;
  }

  function uniformMap(gl, program) {
    const map = {};
    const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < count; i++) {
      const name = gl.getActiveUniform(program, i).name;
      map[name] = gl.getUniformLocation(program, name);
    }
    return map;
  }

  /* Every sample in these shaders is a deliberate lattice lookup — a tone cell
     or an atlas texel — so the hardware must not interpolate between
     neighbours. The one surface that does want filtering is the engrave
     target, which is built to be blown up. */
  function makeTexture(gl, filter) {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  /*
    One canvas gets one kind of context and cannot change its mind, so the
    backend has to be picked before anything is drawn and there is no falling
    back later. Returning null here is what leaves the mode on its 2D
    implementation, and every reason to give up lives in this function.
  */
  function initQuantiseGpu(canvas, mode) {
    const fragment = GPU_FRAGMENT[mode];
    if (!fragment) return null;

    const gl = canvas.getContext('webgl2', {
      /* An unpainted canvas has to stay transparent so the real photograph
         underneath shows through, and readout leans on it for every cell it
         decides not to annotate. */
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      /* The loop repaints only when a new frame lands, so whatever is on the
         canvas has to survive being composited. Without this the driver is
         free to discard it and the background blinks between frames. */
      preserveDrawingBuffer: true,
      powerPreference: 'low-power',
    });
    if (!gl) return null;

    const program = linkProgram(gl, fragment, mode === 'halftone' ? HALFTONE_VS : QUAD_VS);
    /* Engrave thresholds in the small buffer and is then blown up, so it needs
       somewhere to threshold into. Readout draws glyphs at device resolution
       and goes straight to the canvas. */
    const smoothOutput = ['engrave', 'posterise', 'duotone'].includes(mode);
    const colorPass = ['posterise', 'dither', 'duotone', 'riso'].includes(mode);
    const blitProgram = smoothOutput ? linkProgram(gl, BLIT_FS) : null;
    if (!program || (smoothOutput && !blitProgram)) {
      if (program) gl.deleteProgram(program);
      if (blitProgram) gl.deleteProgram(blitProgram);
      const extension = gl.getExtension('WEBGL_lose_context');
      if (extension) extension.loseContext();
      return null;
    }

    const uniforms = uniformMap(gl, program);
    const blitUniforms = blitProgram ? uniformMap(gl, blitProgram) : null;
    const toneTex = makeTexture(gl, mode === 'halftone' ? gl.LINEAR : gl.NEAREST);
    const atlasTex = mode === 'readout' || mode === 'ascii' || mode === 'ascii-soft' ? makeTexture(gl, gl.NEAREST) : null;
    const faultTex = mode === 'halftone' || mode === 'mosaic' ? makeTexture(gl, gl.NEAREST) : null;
    const responseTex = mode !== 'halftone' && mode !== 'mosaic' ? makeTexture(gl, gl.LINEAR) : null;

    let target = null;
    let lost = false;

    /*
      The largest drawing buffer this GPU will actually back. Asking for more
      does not fail loudly — it hands back a canvas that renders nothing, and a
      canvas that renders nothing is a black rectangle once the layers beneath
      it are hidden. Read once: it cannot change for the life of the context.
    */
    const viewportLimit = gl.getParameter(gl.MAX_VIEWPORT_DIMS);
    const maxSize = Math.min(
      gl.getParameter(gl.MAX_TEXTURE_SIZE) || 4096,
      gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) || 4096,
      viewportLimit ? Math.min(viewportLimit[0], viewportLimit[1]) : 4096
    );

    /* Nothing is recoverable without rebuilding every resource on a context
       that may vanish again, and the honest failure for a decorative layer is
       to stop and let the photograph through. Not calling preventDefault is
       what declines the restore. */
    canvas.addEventListener('webglcontextlost', () => {
      lost = true;
    });

    function ensureTarget(w, h) {
      if (target && target.w === w && target.h === h) return;
      if (target) {
        gl.deleteFramebuffer(target.fbo);
        gl.deleteTexture(target.tex);
      }
      /*
        Built on a unit nothing samples from, and that is not a tidiness
        preference.

        Creating a texture binds it to whichever unit is currently active, and
        the caller made unit 0 the tone texture moments earlier. Allocating
        here on unit 0 therefore replaces the tone with this render target, and
        the pass then samples the very texture it is about to draw into. GL
        calls that a feedback loop, answers it with INVALID_OPERATION, and
        discards the draw — silently, since nothing reads getError.

        The visible symptom is a dropped frame rather than an error, and the
        target is only reallocated when the viewport changes, so the frame it
        drops is the first one after every resize. An animated source paints
        again a few milliseconds later and hides it. A still one never paints
        again at all, so the canvas stays empty for the life of the page.
      */
      gl.activeTexture(gl.TEXTURE3);
      const tex = makeTexture(gl, gl.LINEAR);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      const fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.activeTexture(gl.TEXTURE0);
      target = { fbo, tex, w, h };
    }

    /* The tone comes from the same small buffer the 2D modes sample into,
       because a box-filtered downscale is what makes a reduced image read as a
       reduction rather than as a subsample, and drawImage does that on the GPU
       for nothing. Uploading it is the one per-frame cost of this path. */
    function uploadTone(work) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, toneTex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, work);
    }

    return {
      get lost() {
        return lost;
      },

      maxSize,

      /* Called from buildAtlas, so the sheet is uploaded exactly when it is
         rebuilt rather than tested for staleness once a frame. */
      uploadAtlas(sheet) {
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, atlasTex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sheet);
      },

      draw(work, p) {
        if (p.upload !== false) uploadTone(work);
        else {
          // The preceding enlargement pass leaves its target on unit zero.
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, toneTex);
        }
        gl.useProgram(program);

        if (mode === 'mosaic') {
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          gl.viewport(0, 0, canvas.width, canvas.height);
          gl.disable(gl.BLEND);
          gl.uniform1i(uniforms.uTone, 0);
          gl.uniform2f(uniforms.uCanvas, canvas.width, canvas.height);
          gl.uniform2f(uniforms.uGrid, p.cols, p.rows);
          gl.uniform1f(uniforms.uAmount, p.amount);
          gl.uniform1f(uniforms.uSeam, p.seam);
          gl.activeTexture(gl.TEXTURE2);
          gl.bindTexture(gl.TEXTURE_2D, faultTex);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, p.cols, p.rows, 0, gl.RGBA, gl.UNSIGNED_BYTE, p.glints);
          gl.uniform1i(uniforms.uGlints, 2);
          gl.drawArrays(gl.TRIANGLES, 0, 3);
          return;
        }

        if (mode === 'halftone') {
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          gl.viewport(0, 0, canvas.width, canvas.height);
          gl.disable(gl.BLEND);
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, toneTex);
          gl.uniform1i(uniforms.uTone, 0);
          gl.uniform2f(uniforms.uCanvas, canvas.width, canvas.height);
          gl.uniform2f(uniforms.uGrid, p.cols, p.rows);
          gl.uniform1f(uniforms.uPitch, p.pitch);
          gl.uniform1f(uniforms.uTime, p.time);
          gl.uniform1f(uniforms.uMotion, p.motion);
          gl.activeTexture(gl.TEXTURE2);
          gl.bindTexture(gl.TEXTURE_2D, faultTex);
          if (p.uploadFaults) {
            // A row has one byte per bulb, including widths not divisible by four.
            gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, p.cols, p.rows, 0, gl.RED, gl.UNSIGNED_BYTE, p.faults);
            gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
          }
          gl.uniform1i(uniforms.uFaults, 2);
          gl.uniform1f(uniforms.uColors, p.colors);
          gl.uniform3fv(uniforms.uInk, p.ink);
          gl.uniform3fv(uniforms.uInkB, p.inkB);
          gl.uniform3fv(uniforms.uPaper, p.paper);
          gl.clearColor(p.paper[0], p.paper[1], p.paper[2], 1);
          gl.clear(gl.COLOR_BUFFER_BIT);
          gl.enable(gl.BLEND);
          gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
          gl.drawArrays(gl.POINTS, 0, p.cols * p.rows);
          gl.disable(gl.BLEND);
          return;
        }

        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, responseTex);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, p.responseCols, p.responseRows, 0, gl.RED, gl.UNSIGNED_BYTE, p.response);
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
        gl.uniform1i(uniforms.uResponse, 2);

        if (mode === 'engrave' || colorPass) {
          if (smoothOutput) ensureTarget(p.cols, p.rows);
          gl.bindFramebuffer(gl.FRAMEBUFFER, smoothOutput ? target.fbo : null);
          const outW = smoothOutput ? p.cols : canvas.width;
          const outH = smoothOutput ? p.rows : canvas.height;
          gl.viewport(0, 0, outW, outH);
          gl.disable(gl.BLEND);
          gl.uniform1i(uniforms.uTone, 0);
          gl.uniform2f(uniforms.uSize, p.cols, p.rows);
          gl.uniform4f(uniforms.uScreen, p.c0, p.s0, p.c1, p.s1);
          gl.uniform1f(uniforms.uContrast, p.contrast);
          gl.uniform1f(uniforms.uInvert, p.invert ? 1 : 0);
          gl.uniform2f(uniforms.uGrid, p.cols, p.rows);
          gl.uniform2f(uniforms.uOutput, outW, outH);
          gl.uniform2f(uniforms.uCanvas, canvas.width, canvas.height);
          gl.uniform1f(uniforms.uMode, p.mode);
          gl.uniform1f(uniforms.uSteps, p.steps);
          gl.uniform1f(uniforms.uDrift, p.drift);
          gl.uniform3fv(uniforms.uInk, p.ink);
          if (colorPass) gl.uniform3fv(uniforms.uInkB, p.inkB);
          gl.uniform3fv(uniforms.uPaper, p.paper);
          gl.drawArrays(gl.TRIANGLES, 0, 3);

          if (!smoothOutput) return;

          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          gl.viewport(0, 0, canvas.width, canvas.height);
          gl.useProgram(blitProgram);
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, target.tex);
          gl.uniform1i(blitUniforms.uSrc, 0);
          gl.uniform2f(blitUniforms.uCanvas, canvas.width, canvas.height);
          gl.drawArrays(gl.TRIANGLES, 0, 3);
          return;
        }

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, canvas.width, canvas.height);
        /* Most cells are empty and the photograph under them has to survive,
           so the pass clears to nothing and blends what it does draw. The
           atlas arrives unpremultiplied, hence the multiply in the shader and
           ONE rather than SRC_ALPHA here. */
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, atlasTex);
        gl.uniform1i(uniforms.uTone, 0);
        gl.uniform1i(uniforms.uAtlas, 1);
        gl.uniform2f(uniforms.uGrid, p.cols, p.rows);
        gl.uniform2f(uniforms.uCell, p.cellW, p.cellH);
        gl.uniform2f(uniforms.uCanvas, canvas.width, canvas.height);
        gl.uniform2f(uniforms.uAtlasSize, p.atlasW, p.atlasH);
        gl.uniform1f(uniforms.uGlyphs, p.glyphs);
        gl.uniform1f(uniforms.uFloor, p.floor);
        gl.uniform1f(uniforms.uSpan, p.span);
        gl.uniform1f(uniforms.uOverlay, mode === 'readout' ? 1 : 0);
        gl.uniform1f(uniforms.uSoftAscii, mode === 'ascii-soft' ? 1 : 0);
        gl.uniform1f(uniforms.uInvert, p.invert ? 1 : 0);
        gl.uniform3fv(uniforms.uPaper, p.paper);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      },

      clear() {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
      },

      destroy() {
        if (lost) return;
        [toneTex, atlasTex, faultTex, responseTex, target && target.tex].filter(Boolean).forEach(tex => gl.deleteTexture(tex));
        if (target) gl.deleteFramebuffer(target.fbo);
        gl.deleteProgram(program);
        if (blitProgram) gl.deleteProgram(blitProgram);
        // Editor re-renders should release their context slot immediately.
        const extension = gl.getExtension('WEBGL_lose_context');
        if (extension) extension.loseContext();
      },
    };
  }

  /* A gesture excites individual cells, then leaves them to finish. Distances
     are CSS pixels, so changing dot/pixel size does not change the mouse's
     reach. A bounded polyline retains turns between painted frames. */
  function createCellResponse(motion, random = Math.random) {
    const field = { cols: 0, rows: 0, states: new Uint8Array(0), pulses: new Map(), dirty: true };
    let previous = null;
    let pending = [];
    let distance = 0;
    let lastSeed = -Infinity;
    let serial = 0;

    field.release = () => { previous = null; pending = []; distance = 0; };
    field.reset = () => {
      field.release();
      field.pulses.clear();
      field.states.fill(0);
      field.dirty = true;
      lastSeed = -Infinity;
    };
    field.move = (x, y, now, width, height) => {
      if (previous && Math.hypot((x - previous.x) * width, (y - previous.y) * height) < 1.5) return false;
      const point = { x, y, now };
      const from = previous && now - previous.now < 0.18 ? previous : point;
      const dx = (x - from.x) * width, dy = (y - from.y) * height;
      const length = Math.hypot(dx, dy);
      const speed = Math.min(1, length / Math.max(0.008, now - from.now) / 1500);
      const ux = length ? dx / length : 1, uy = length ? dy / length : 0;
      const turn = previous && length ? (1 - (ux * previous.ux + uy * previous.uy)) * 0.5 : 0;
      pending.push({ from, to: point, length, speed, ux, uy, turn: turn || 0 });
      if (pending.length > 24) pending.shift();
      previous = { ...point, ux, uy };
      return true;
    };

    function excite(gesture, t, now, width, height) {
      const { cols, rows, pulses } = field;
      const cellW = width / cols, cellH = height / rows;
      const ox = (gesture.from.x + (gesture.to.x - gesture.from.x) * t) * width;
      const oy = (gesture.from.y + (gesture.to.y - gesture.from.y) * t) * height;
      const energy = gesture.speed;
      const reach = (60 + motion * 95 + energy * 150) * Math.min(1, Math.min(width, height) / 600);
      const count = 3 + Math.round(motion * 5 + energy * 5 + gesture.turn * 3);
      const limit = Math.min(1800, Math.max(48, Math.floor(cols * rows * 0.1)));
      serial++;
      for (let n = 0; n < count; n++) {
        // A wake, not a disc. Some sparks sit ahead, most fall behind the hand.
        const along = (random() * 1.4 - 0.95) * reach;
        const across = (random() - random()) * reach * (0.4 + gesture.turn * 0.6);
        let x = Math.floor((ox + gesture.ux * along - gesture.uy * across) / cellW);
        let y = Math.floor((oy + gesture.uy * along + gesture.ux * across) / cellH);
        const length = 1 + Math.floor(random() * (3 + energy * 7 + motion * 4));
        const horizontal = random() < Math.abs(gesture.ux) * 0.65 + 0.175;
        const sx = gesture.ux < 0 ? -1 : 1, sy = gesture.uy < 0 ? -1 : 1;
        const kind = random();
        const delay = random() * (0.04 + energy * 0.14);
        const duration = 0.2 + random() * 0.55 + energy * 0.12;
        const echo = random() < 0.32;
        for (let j = 0; j < length; j++) {
          if (x >= 0 && x < cols && y >= 0 && y < rows && pulses.size < limit) {
            const index = y * cols + x;
            // Each cell has a stable resistance; gaps break up the circuit.
            const resistance = ((Math.imul(x + 7, 374761393) ^ Math.imul(y + 3, 668265263)) >>> 0) / 4294967295;
            if (!pulses.has(index) && (j === 0 || resistance > 0.18)) {
              pulses.set(index, {
                born: now + delay + j * (0.012 + (1 - energy) * 0.018),
                duration: duration * (0.75 + random() * 0.5),
                kind, echo, phase: random(),
                dx: sx * (1 + ((serial + j) % 4)),
                dy: sy * (1 + ((serial + n) % 3)),
                ux: gesture.ux, uy: gesture.uy,
              });
            }
          }
          // Mostly aligned with the hand; bends and gaps keep
          // repeated strokes from stamping the same row-shaped motif.
          if (random() < 0.23) { if (horizontal) y += sy; else x += sx; }
          else if (horizontal) x += sx;
          else y += sy;
        }
      }
    }

    field.update = (cols, rows, width, height, now) => {
      if (field.cols !== cols || field.rows !== rows) {
        field.reset();
        field.cols = cols; field.rows = rows;
        field.states = new Uint8Array(cols * rows);
      }
      let budget = 6;
      const spacing = 22 + motion * 14;
      for (const gesture of pending) {
        distance += gesture.length;
        if (!budget || (distance < spacing && now - lastSeed < 0.075)) continue;
        const count = Math.min(budget, Math.max(1, Math.floor(distance / spacing)));
        for (let i = 0; i < count; i++) excite(gesture, (i + 1) / count, now, width, height);
        budget -= count;
        distance %= spacing;
        lastSeed = now;
      }
      pending = [];
      for (const [index, pulse] of field.pulses) {
        const age = (now - pulse.born) / pulse.duration;
        let state = 0;
        if (age >= 1) field.pulses.delete(index);
        else if (age >= 0) {
          if (pulse.kind < 0.12) state = age < 0.5 ? 1 : 4;
          else if (pulse.kind < 0.3) state = age < 0.28 ? 4 : age < 0.62 ? 3 : 2;
          else state = age < 0.2 + pulse.phase * 0.25 ? 3 : 4;
          if (pulse.echo && age > 0.45 && age < 0.62) state = 1;
          if (pulse.echo && age >= 0.62 && age < 0.79) state = 3;
        }
        if (field.states[index] !== state) {
          field.states[index] = state;
          field.dirty = true;
        }
      }
    };
    return field;
  }

  // One small texel per tile: strength, progress and the hand's direction.
  // The shared gesture field supplies the staggered runs; Mosaic replaces its
  // hard relay states with a single, smooth reflection and an exact return.
  function mosaicGlints(field, now, motion, bytes) {
    const length = field.cols * field.rows * 4;
    if (!bytes || bytes.length !== length) bytes = new Uint8Array(length);
    else bytes.fill(0);
    if (motion <= 0) return bytes;
    for (const [index, pulse] of field.pulses) {
      const age = (now - pulse.born) / pulse.duration;
      if (age <= 0 || age >= 1) continue;
      const envelope = Math.sin(Math.PI * age) ** 2;
      const strength = envelope * (0.3 + motion * 0.7) * (0.65 + pulse.phase * 0.35);
      const offset = index * 4;
      bytes[offset] = Math.round(strength * 255);
      bytes[offset + 1] = Math.round(age * 255);
      bytes[offset + 2] = Math.round(128 + pulse.ux * 127);
      bytes[offset + 3] = Math.round(128 + pulse.uy * 127);
    }
    return bytes;
  }

  function startQuantise(canvas) {
    if (canvas.__bgQuantiseCleanup) return;
    canvas.__bgQuantiseReady = false;
    const root = canvas.closest('.bg-root');
    const source = root && root.querySelector('img.bg-media, video.bg-media, canvas.bg-media');
    const mode = canvas.dataset.bgQuantise;
    const softAscii = mode === 'ascii-soft';
    // Soft glyphs keeps the source's low-key colour instead of terminal-style
    // brightness normalisation. The original ramp and palette stay unchanged.
    const glyphs = softAscii ? ' .+xXS08@' : GLYPHS;
    /* Before any context is created, because creating one is the thing that
       cannot be taken back. */
    if (!source) return;
    /* Which backend runs is settled here for the same reason. */
    const gpu = initQuantiseGpu(canvas, mode);
    /* Alpha deliberately left on. An unpainted canvas is then transparent and
       the real photograph underneath shows through, where an opaque one would
       cover it with a black rectangle. */
    const ctx = gpu ? null : canvas.getContext('2d');
    if (!ctx && !gpu) {
      root.removeAttribute('data-bg-quantise-covers');
      return;
    }
    const isVideo = source.tagName === 'VIDEO';
    /* A generated background: snippets/vector-bg draws the media layer itself,
       so the frame source is another canvas rather than a decoded file. It is
       CORS-clean by construction and it never has a loading state. */
    const isCanvas = source.tagName === 'CANVAS';

    const amount = Math.min(Math.max(parseFloat(canvas.dataset.bgQuantiseAmount) || 0, 0), 100) / 100;
    const usePixelPalette = canvas.hasAttribute('data-bg-pixel-palette');
    const inkA = parseHex(canvas.dataset.bgInkA, [26, 26, 26]);
    const inkB = parseHex(canvas.dataset.bgInkB, [255, 77, 109]);
    const paper = parseHex(canvas.dataset.bgPaper, [244, 241, 232]);
    /* Which way the line screen runs. A dark stock cannot be a print, so the
       strokes emit instead of absorbing — see engrave(). */
    const paperIsLight = luma(paper[0], paper[1], paper[2]) > 128;
    /* The ground text mode prints onto. Kept separate from the riso stock
       because the two conventions point opposite ways — a press prints dark ink
       on cream, a terminal prints light glyphs on black — and one shared colour
       would have to get one of them wrong by default. */
    const asciiPaper = parseHex(canvas.dataset.bgAsciiPaper, [0, 0, 0]);
    const asciiPaperCss = `rgb(${asciiPaper[0]},${asciiPaper[1]},${asciiPaper[2]})`;
    const asciiPaperIsLight = luma(asciiPaper[0], asciiPaper[1], asciiPaper[2]) > 128;
    const animated = canvas.dataset.bgAnimated === 'true';
    const halftoneSize = Math.max(4, Math.min(20, Number(canvas.dataset.bgHalftoneSize) || 8));
    const halftoneMotion = clamp01(Number(canvas.dataset.bgHalftoneMotion ?? 40) / 100);
    const halftoneColors = ({ electric: 0, image: 1, custom: 2 })[canvas.dataset.bgHalftoneColors] ?? 0;
    const halftoneInkA = parseHex(canvas.dataset.bgHalftoneInkA, [216, 69, 197]);
    const halftoneInkB = parseHex(canvas.dataset.bgHalftoneInkB, [153, 248, 249]);
    const halftonePaper = halftoneColors === 0 ? [27, 1, 245] : parseHex(canvas.dataset.bgHalftonePaper, [0, 0, 0]);
    const pixelMotion = clamp01(Number(canvas.dataset.bgPixelMotion ?? 40) / 100);
    const quantiseMotion = clamp01(Number(canvas.dataset.bgQuantiseMotion ?? 40) / 100);
    const mosaicSize = Math.max(2, Math.min(16, Number(canvas.dataset.bgMosaicSize) || 5));
    const responseMotion = mode === 'halftone' ? halftoneMotion : mode === 'pixelate' ? pixelMotion : quantiseMotion;
    const effectMotion = responseMotion > 0;
    let effectTime = 0;
    let toneDirty = true;
    let halftonePixels = null;
    let pixelPixels = null;
    let pixelResponseFrame = null;
    let mosaicResponse = null;
    let posterLut = null;
    let duotoneLut = null;
    let risoFrames = null;
    const finePointer = window.matchMedia('(any-hover: hover) and (any-pointer: fine)');
    const faults = createCellResponse(responseMotion);
    const cursorEvents = new AbortController();
    const releaseCursor = () => faults.release();
    const resetCursor = () => faults.reset();
    function moveCursor(event) {
      if (event.pointerType === 'touch' || !finePointer.matches || reduced.matches || document.hidden) {
        releaseCursor();
        return;
      }
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height || event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        releaseCursor();
        return;
      }
      if (faults.move((event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height,
        performance.now() / 1000, rect.width, rect.height)) faults.dirty = true;
    }
    function updateFaults(cols, rows) {
      if (reduced.matches || !effectMotion) resetCursor();
      faults.update(cols, rows, canvas.clientWidth || window.innerWidth, canvas.clientHeight || window.innerHeight, effectTime);
    }
    let responseBytes = new Uint8Array(0);
    let responsePixels = null;
    let responseColumns = null;
    function prepareResponse(cols, rows) {
      const width = canvas.clientWidth || window.innerWidth;
      const height = canvas.clientHeight || window.innerHeight;
      const responseCols = cols || Math.ceil(width / 14);
      const responseRows = rows || Math.ceil(height / 14);
      updateFaults(responseCols, responseRows);
      if (responseBytes.length !== responseCols * responseRows) responseBytes = new Uint8Array(responseCols * responseRows);
      responseBytes.fill(128);
      for (const [index, pulse] of faults.pulses) {
        const age = (effectTime - pulse.born) / pulse.duration;
        if (age <= 0 || age >= 1) continue;
        // Color bands and print marks need a soft rise and recovery, while the
        // existing bulbs/pixels retain their discrete relay states.
        const envelope = Math.sin(Math.PI * age) ** 2;
        const charge = envelope * (0.55 + pulse.phase * 0.45) * (0.5 + responseMotion * 0.5);
        responseBytes[index] = Math.round(128 + (pulse.kind < 0.4 ? -1 : 1) * charge * 127);
      }
      faults.dirty = false;
      return { response: responseBytes, responseCols, responseRows };
    }
    function responseRaster(cols, rows) {
      if (!faults.pulses.size) return null;
      if (!responsePixels || responsePixels.length !== cols * rows) responsePixels = new Float32Array(cols * rows);
      const fw = faults.cols, fh = faults.rows;
      responsePixels.fill(0);
      if (!responseColumns || responseColumns.cols !== cols || responseColumns.fw !== fw) {
        const x0 = new Int32Array(cols), x1 = new Int32Array(cols), t = new Float32Array(cols);
        for (let x = 0; x < cols; x++) {
          const fx = Math.max(0, Math.min(fw - 1, (x + 0.5) * fw / cols - 0.5));
          x0[x] = Math.floor(fx); x1[x] = Math.min(fw - 1, x0[x] + 1); t[x] = fx - x0[x];
        }
        responseColumns = { cols, fw, x0, x1, t };
      }
      // Most of the field is neutral. Interpolate only the touched spans, so
      // CPU fallbacks do not pay for a second full-image pass on every gesture.
      const left = new Int32Array(fh).fill(fw), right = new Int32Array(fh).fill(-1);
      for (let i = 0; i < responseBytes.length; i++) {
        if (responseBytes[i] === 128) continue;
        const y = Math.floor(i / fw), x = i % fw;
        left[y] = Math.min(left[y], x); right[y] = Math.max(right[y], x);
      }
      const { x0, x1, t } = responseColumns;
      for (let y = 0; y < rows; y++) {
        const fy = Math.max(0, Math.min(fh - 1, (y + 0.5) * fh / rows - 0.5));
        const y0 = Math.floor(fy), y1 = Math.min(fh - 1, y0 + 1), ty = fy - y0;
        const lo = Math.min(left[y0], left[y1]), hi = Math.max(right[y0], right[y1]);
        if (hi < lo) continue;
        const from = Math.max(0, Math.floor((lo - 0.5) * cols / fw - 0.5));
        const to = Math.min(cols, Math.ceil((hi + 1.5) * cols / fw));
        const a = y0 * fw, b = y1 * fw;
        for (let x = from; x < to; x++) {
          const top = lerp(responseBytes[a + x0[x]], responseBytes[a + x1[x]], t[x]);
          const bottom = lerp(responseBytes[b + x0[x]], responseBytes[b + x1[x]], t[x]);
          const value = (lerp(top, bottom, ty) - 128) / 127;
          responsePixels[y * cols + x] = Math.abs(value) < 0.008 ? 0 : value;
        }
      }
      return responsePixels;
    }
    if (effectMotion) {
      const options = { passive: true, signal: cursorEvents.signal };
      window.addEventListener('pointermove', moveCursor, options);
      window.addEventListener('pointerout', (event) => { if (!event.relatedTarget) releaseCursor(); }, options);
      window.addEventListener('pointercancel', releaseCursor, options);
      window.addEventListener('blur', releaseCursor, options);
      finePointer.addEventListener('change', resetCursor, options);
    }

    /* The shaders want 0-1. Built once rather than per frame, since a colour
       cannot change without the section re-rendering. */
    const unit = (rgb) => new Float32Array([rgb[0] / 255, rgb[1] / 255, rgb[2] / 255]);
    const glInk = gpu ? unit(inkA) : null;
    const glInkB = gpu ? unit(inkB) : null;
    const glPaper = gpu ? unit(paper) : null;
    const glAsciiPaper = gpu ? unit(asciiPaper) : null;

    const work = document.createElement('canvas');
    /* Asking to read this buffer back keeps it on the CPU, which is right for
       the 2D modes and exactly wrong for the GPU ones: they never read it, they
       upload it, and a CPU-backed source turns that into a copy across the bus
       every frame. */
    const wctx = work.getContext('2d', { willReadFrequently: !gpu });
    if (!wctx) return;

    /* A second scratch buffer, because riso builds a plate while the sampled
       image is still needed to build the next one. */
    const plate = document.createElement('canvas');
    const pctx = plate.getContext('2d');

    let atlas = null;
    let atlasCell = [0, 0];
    /* Last frame's packed (glyph, ink) per cell, so an animated text mode can
       repaint only the cells that actually changed. */
    let prevCells = null;
    /* The palette in force, and how well it has been fitting. A GIF carries one
       global colour table for every frame of its animation, and this is the
       same idea arrived at the same way: build a table, then hold it. See
       pixelate() for what unseats it. */
    let pixelPalette = null;
    let pixelLight = null;
    let pixelDark = null;
    let paletteProbedAt = 0;

    let scale = 1;

    /* ------------------------------------------------------------------
       Where the pixels come from.

       An <img> holding an animated GIF is not a frame source at all, and the
       reason is worth stating precisely because it looks like a throttling
       problem and is not one. Canvas is specified to draw an animated image's
       *default image* — the frame the format nominates for when animation is
       unavailable — and never the frame currently on screen. So drawImage on a
       playing GIF returns frame 0 forever, on every browser, no matter how
       visible the element is.

       Measured here against a 34-frame 70ms animation, with the page at a
       steady 60fps and the element full-size, unobscured and on top:
       drawImage returned one distinct result across 3s, and its hash was
       byte-identical to decoded frame 0. createImageBitmap did the same. No
       amount of keeping the element painted changes it.

       So the animation is driven by decoding the bytes ourselves. Frames are
       decoded on demand and clocked off their own durations, which also gets
       the GIF's real timing instead of a fixed guess at it.
       ------------------------------------------------------------------ */

    /* What sample() draws and the size it reasons about. For a still image and
       for video these stay pointed at the element; for a decoded animation the
       frame is swapped underneath. */
    let frame = source;
    let frameW = 0;
    let frameH = 0;
    let dirty = true;
    /* Polled from the paint loop, for the one provider with no callback to be
       given: video without requestVideoFrameCallback. */
    let advance = null;
    /* { pause, resume, destroy }, once whichever provider applies has set up. */
    let provider = null;
    let providerWanted = false;

    function frameSize() {
      if (isVideo) return [source.videoWidth, source.videoHeight];
      if (isCanvas) return [source.width, source.height];
      return [source.naturalWidth, source.naturalHeight];
    }

    function setFrame(next, w, h) {
      /* A VideoFrame holds a decoder buffer open until it is closed, and the
         decoder has a small pool of them. Leaking one stalls playback. */
      if (frame !== next && frame && typeof frame.close === 'function') frame.close();
      frame = next;
      toneDirty = true;
      frameW = w;
      frameH = h;
      dirty = true;
    }

    /* Video needs no decoding — the element is a genuine frame source, and
       requestVideoFrameCallback fires once per presented frame, so there is
       nothing to poll and nothing to guess. */
    function driveVideo() {
      const rvfc = source.requestVideoFrameCallback && source.requestVideoFrameCallback.bind(source);
      if (!rvfc) {
        /* Firefox. currentTime is still exact, so poll it off the paint loop
           rather than hashing pixels to discover the same fact. */
        let seen = -1;
        advance = () => {
          if (source.currentTime === seen) return;
          seen = source.currentTime;
          const [w, h] = frameSize();
          if (w) setFrame(source, w, h);
        };
        return { pause() {}, resume() {}, destroy() { advance = null; } };
      }
      let handle = 0;
      const onFrame = () => {
        const [w, h] = frameSize();
        if (w) setFrame(source, w, h);
        handle = rvfc(onFrame);
      };
      const cancel = () => {
        if (handle) source.cancelVideoFrameCallback(handle);
        handle = 0;
      };
      handle = rvfc(onFrame);
      return {
        pause: cancel,
        resume() { if (!handle) handle = rvfc(onFrame); },
        destroy: cancel
      };
    }

    /*
      A generated field. There is no decoder and no callback to subscribe to —
      the renderer paints on its own rAF and this pass samples whatever is in
      the buffer, one frame behind at worst.

      It reads `running` off the renderer instead of marking every frame dirty,
      because a field that is not animating produces identical pixels and
      re-quantising them is pure cost. The blank effect draws once, and reduced
      motion freezes the hyperspace field the same way; both then sit at zero
      until the viewport changes shape.
    */
    function driveCanvas() {
      advance = () => {
        const live = source.__vectorBg;
        const [w, h] = frameSize();
        if (!w) return;
        if (live && !live.running && w === frameW && h === frameH) return;
        setFrame(source, w, h);
      };
      return { pause() {}, resume() {}, destroy() { advance = null; } };
    }

    /*
      Animated image. Decoded rather than observed, for the reason above.

      Returning null is what says "this source cannot be animated", and the
      caller answers it by painting one frame and shutting the loop down. That
      is the whole of the fallback, and it replaces one that polled the element
      20 times a second: since drawImage only ever yields frame 0, every one of
      those passes re-quantised the same frame, so the background was frozen
      *and* paying for a full pass per tick to stay that way.

      Who lands here: ImageDecoder is Chrome/Edge 94+, Firefox 130+ desktop and
      Safari 26+, so it is Safari 18 and earlier — every iPhone not yet updated
      — plus Firefox for Android. They get the quantised first frame as a still.

      That is the right way to lose, and the choice is not obvious, so: the
      alternative is giving the effect up and letting the untreated GIF play,
      which is what a source this pass cannot read normally gets. It is wrong
      here. Quantise is the store's chosen look, and the motion is decoration
      on top of it — a shopper on an old iPhone seeing a sharp full-colour
      photograph where everyone else sees six flat colours is a different shop.
      Losing the motion keeps the identity; losing the identity to keep the
      motion does not.
    */
    async function driveAnimatedImage() {
      if (typeof ImageDecoder === 'undefined') return null;
      let decoder;
      try {
        /*
          The Accept header is very nearly the whole cost of this request.
          Shopify's CDN negotiates format on it and answers Vary: Accept, so a
          fetch left to send the wildcard default is asking for the original
          upload: measured on one background, 18.45MB of GIF against the 2.87MB
          animated WebP the <img> beside it had already downloaded, for the
          same 678x382 frame and the same 140 of them. Naming the image formats
          asks for the variant the element got instead.
        */
        const res = await fetch(source.currentSrc || source.src, {
          mode: 'cors',
          credentials: 'omit',
          headers: { Accept: 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8' },
        });
        if (!res.ok) return null;
        const type = res.headers.get('content-type') || '';
        if (!(await ImageDecoder.isTypeSupported(type))) return null;
        decoder = new ImageDecoder({ data: await res.arrayBuffer(), type });
        await decoder.tracks.ready;
      } catch (e) {
        return null;
      }

      /* A still image has nothing to advance to, so one frame is the whole
         truth and freezing on it is correct. */
      const track = decoder.tracks.selectedTrack;
      if (!track || !track.animated || track.frameCount < 2) {
        decoder.close();
        return null;
      }

      let index = 0;
      let timer = 0;
      let dead = false;
      let running = false;

      async function step() {
        if (dead || !running) return;
        let result;
        try {
          result = await decoder.decode({ frameIndex: index });
        } catch (e) {
          dead = true;
          try { decoder.close(); } catch (e2) { /* already closed */ }
          return;
        }
        if (dead || !running) { result.image.close(); return; }
        setFrame(result.image, result.image.displayWidth, result.image.displayHeight);
        /* duration is microseconds and is occasionally absent; 70ms is the
           most common GIF delay and a sane floor for a runaway one. */
        const ms = Math.max(20, Math.round((result.image.duration || 70000) / 1000));
        index = (index + 1) % track.frameCount;
        timer = setTimeout(step, ms);
      }

      running = true;
      step();

      return {
        pause() { running = false; clearTimeout(timer); },
        /* The decoder is kept open across a pause. Re-fetching and re-parsing
           the whole file to resume from a tab switch would cost more than the
           frames it saves. */
        resume() { if (dead || running) return; running = true; step(); },
        destroy() {
          dead = true;
          running = false;
          clearTimeout(timer);
          try { decoder.close(); } catch (e) { /* already closed */ }
        }
      };
    }

    function fit() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      /* A window stretched across a second display can ask for a buffer no GPU
         will back, and the GL path answers that with a canvas that draws
         nothing rather than an error. Clamping keeps the effect: the layer is
         object-fit: cover, so an over-large viewport gets a slightly softer
         engraving instead of a black rectangle. */
      const limit = gpu ? gpu.maxSize : 8192;
      const w = Math.min(limit, Math.max(1, Math.round((canvas.clientWidth || window.innerWidth) * dpr)));
      const h = Math.min(limit, Math.max(1, Math.round((canvas.clientHeight || window.innerHeight) * dpr)));
      scale = dpr;
      if (canvas.width === w && canvas.height === h) return false;
      canvas.width = w;
      canvas.height = h;
      atlas = null;
      toneDirty = true;
      prevCells = null;
      return true;
    }

    /* Draw the source into the work buffer at cols x rows, cover-cropped.
       Smoothing stays on for this step: a box-filtered downscale averages the
       block, and averaging is what makes a 40px-wide buffer read as a faithful
       reduction rather than a subsample of stray pixels. */
    function sample(cols, rows, stretch) {
      /* Assigning either dimension reallocates and clears the backing store
         even when the value is unchanged, which is a buffer rebuild every
         frame for a grid that only moves when the viewport does. */
      if (work.width !== cols || work.height !== rows) {
        work.width = cols;
        work.height = rows;
      }
      const ar = frameW / frameH;
      const [dx, dy, dw, dh] = coverDest(ar, cols, rows, stretch);
      wctx.imageSmoothingEnabled = true;
      /* Replace rather than draw over. Reallocating the buffer used to clear it
         as a side effect, and a GIF frame is allowed to carry transparency —
         composited normally it would then show the previous frame through its
         own holes. */
      wctx.globalCompositeOperation = 'copy';
      wctx.drawImage(frame, dx, dy, dw, dh);
      wctx.globalCompositeOperation = 'source-over';
    }

    function blowUp(smooth) {
      ctx.imageSmoothingEnabled = smooth;
      ctx.globalCompositeOperation = 'copy';
      ctx.drawImage(work, 0, 0, work.width, work.height, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    }

    function sampleTone(cols, rows, stretch) {
      // CPU color passes rewrite work; GPU passes can retain a still source.
      const refresh = !gpu || toneDirty || work.width !== cols || work.height !== rows;
      if (refresh) sample(cols, rows, stretch);
      toneDirty = false;
      return refresh;
    }

    function colorGpu(modeId, cols, rows, settings) {
      const upload = sampleTone(cols, rows);
      const response = prepareResponse();
      gpu.draw(work, {
        upload, cols, rows, mode: modeId, steps: 1, contrast: 1, drift: 0,
        ink: glInk, inkB: glInkB, paper: glPaper, ...settings, ...response,
      });
    }

    function gridFor(cellsAcross) {
      // No print screen can resolve more cells than there are output pixels.
      // This also bounds portrait/very narrow previews to the backing buffer.
      const cols = Math.max(1, Math.min(canvas.width, Math.max(8, Math.round(cellsAcross))));
      const rows = Math.max(1, Math.min(canvas.height, Math.max(6, Math.round((cols * canvas.height) / canvas.width))));
      return [cols, rows];
    }

    /*
      Six colours pulled out of the frame, chosen to keep the picture's own
      colours rather than average them into a wash.

      What separates it from a plain median cut is only the last step, and that
      step is the whole difference between isolating a picture's colours and
      blending them. See the comment above the representative below.

      The work buffer is already the pixel grid the shopper sees, so extracting
      the palette here is both more honest and much cheaper than reading the
      full-resolution source. Bucketing each channel to four bits caps the sort
      at 4096 entries without choosing the final colours for it: each bucket
      keeps the mean of the real samples that landed there.
    */
    function extractPixelPalette(data, count) {
      const histogram = new Map();
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 16) continue;
        const key = ((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4);
        let bin = histogram.get(key);
        if (!bin) {
          bin = { r: 0, g: 0, b: 0, n: 0, key };
          histogram.set(key, bin);
        }
        bin.r += data[i];
        bin.g += data[i + 1];
        bin.b += data[i + 2];
        bin.n++;
      }

      const colours = Array.from(histogram.values(), (bin) => ({
        r: bin.r / bin.n,
        g: bin.g / bin.n,
        b: bin.b / bin.n,
        n: bin.n,
        key: bin.key,
      }));
      if (!colours.length) return [];

      function measure(entries) {
        let r0 = 255;
        let g0 = 255;
        let b0 = 255;
        let r1 = 0;
        let g1 = 0;
        let b1 = 0;
        let weight = 0;
        for (const colour of entries) {
          r0 = Math.min(r0, colour.r);
          g0 = Math.min(g0, colour.g);
          b0 = Math.min(b0, colour.b);
          r1 = Math.max(r1, colour.r);
          g1 = Math.max(g1, colour.g);
          b1 = Math.max(b1, colour.b);
          weight += colour.n;
        }
        const ranges = [r1 - r0, g1 - g0, b1 - b0];
        const channel = ranges.indexOf(Math.max(...ranges));
        return { channel, weight, score: ranges[channel] * Math.sqrt(weight) };
      }

      const boxes = [{ entries: colours, ...measure(colours) }];
      while (boxes.length < count) {
        let splitIndex = -1;
        let splitScore = -1;
        for (let i = 0; i < boxes.length; i++) {
          if (boxes[i].entries.length > 1 && boxes[i].score > splitScore) {
            splitIndex = i;
            splitScore = boxes[i].score;
          }
        }
        if (splitIndex < 0) break;

        const box = boxes.splice(splitIndex, 1)[0];
        const channel = ['r', 'g', 'b'][box.channel];
        box.entries.sort((a, b) => a[channel] - b[channel] || a.key - b.key);
        const halfway = box.weight / 2;
        let accumulated = 0;
        let cut = 1;
        for (; cut < box.entries.length; cut++) {
          accumulated += box.entries[cut - 1].n;
          if (accumulated >= halfway) break;
        }
        cut = Math.min(cut, box.entries.length - 1);
        const first = box.entries.slice(0, cut);
        const second = box.entries.slice(cut);
        boxes.push({ entries: first, ...measure(first) }, { entries: second, ...measure(second) });
      }

      /*
        Each box reports the colour it is densest around rather than the average
        of everything in it. A box holding sky and foliage averages to a teal
        that appears nowhere in the frame, whereas its densest neighbourhood is
        the sky, and the sky is a colour the shopper can point at. On SMPTE bars
        the average returns a dull pink for the red bar and a yellow-green for
        white; the mode returns every bar exactly. This one line is what makes
        the mode a colour isolator rather than a colour averager, and it is
        enough on its own — weighting the split by chroma and pushing saturation
        back into the result were both tried here and neither moved a palette by
        more than three units out of 255.

        The neighbourhood runs about two histogram bins wide, far enough to
        average out the noise at a bin boundary and not far enough to reach a
        second hue. At four bins the reference photograph's slate sky (hue 207)
        swings round to teal (hue 166).
      */
      const palette = boxes.map((box) => {
        let peak = box.entries[0];
        for (const colour of box.entries) {
          if (colour.n > peak.n) peak = colour;
        }
        const seed = [peak.r, peak.g, peak.b];

        let r = 0;
        let g = 0;
        let b = 0;
        let weight = 0;
        for (const colour of box.entries) {
          if (colourDistance(colour.r, colour.g, colour.b, seed[0], seed[1], seed[2]) > PALETTE_MODE_RADIUS) continue;
          r += colour.r * colour.n;
          g += colour.g * colour.n;
          b += colour.b * colour.n;
          weight += colour.n;
        }
        return [Math.round(r / weight), Math.round(g / weight), Math.round(b / weight)];
      });

      /* A deterministic order gives slot matching a dependable starting set. */
      palette.sort((a, b) => luma(a[0], a[1], a[2]) - luma(b[0], b[1], b[2]) || a[0] - b[0] || a[1] - b[1]);
      return palette;
    }

    /*
      Redmean: a cheap approximation of perceptual distance that, unlike
      weighting the channels by their contribution to luminance, actually
      separates hues. Luminance weighting scores a saturated red and a grey of
      the same brightness as close neighbours, so cells snap across hue and the
      picture loses its colours — the exact opposite of the job here.

      Six scalars rather than two triples, because the mapping pass calls this
      once per palette slot per cell, and a triple in the argument list is an
      array allocated tens of thousands of times a frame to be read three times.
    */
    function colourDistance(r, g, b, r2, g2, b2) {
      const mean = (r + r2) / 2;
      const dr = r - r2;
      const dg = g - g2;
      const db = b - b2;
      return (2 + mean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - mean) / 256) * db * db;
    }

    /*
      The palette in force, flattened to one contiguous run of bytes so the
      mapping loop reads it without dereferencing six little arrays.

      A lookup table was tried here and rejected, which is worth recording
      because it is the obvious optimisation and it looks free. Keying a cache
      on the cell's colour truncated to five bits per channel took the mapping
      pass from 0.68ms to 0.15ms on a 220x131 grid — and put 9.5% of cells on
      the wrong slot, because this mode's palettes are often tight: four of the
      six colours the reference GIF yields sit within 15 units of each other,
      and a five-bit key smears the boundary between them by up to eight. Six
      bits halved the error to 3.9% and cost 512KB. Neither is worth it: the
      whole pass runs at 1.1ms against a 70ms GIF frame, so it already spends
      under 2% of wall time, and half a millisecond nobody can perceive does
      not buy a ragged edge between two flat colours.
    */
    let paletteFlat = null;

    function setPixelPalette(palette) {
      pixelPalette = palette;
      const byLight = [...palette].sort((a, b) => luma(...a) - luma(...b));
      pixelDark = byLight[0];
      pixelLight = byLight[byLight.length - 1];
      paletteFlat = new Uint8Array(palette.length * 3);
      for (let i = 0; i < palette.length; i++) {
        paletteFlat[i * 3] = palette[i][0];
        paletteFlat[i * 3 + 1] = palette[i][1];
        paletteFlat[i * 3 + 2] = palette[i][2];
      }
    }

    /* Squared distance to the nearest entry of a palette held as triples. Used
       only to score a palette, never to apply one. */
    function nearestDistance(r, g, b, palette) {
      let best = Infinity;
      for (let p = 0; p < palette.length; p++) {
        const entry = palette[p];
        const d = colourDistance(r, g, b, entry[0], entry[1], entry[2]);
        if (d < best) best = d;
      }
      return best;
    }

    /*
      The mean distance from each cell to its nearest slot, which is a palette's
      fit. Both candidates are weighed in one walk of the grid because the only
      caller compares them against each other, and two walks read every cell
      twice to answer one question.
    */
    function measureFits(data, challenger, incumbent) {
      let challengerTotal = 0;
      let incumbentTotal = 0;
      let counted = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 16) continue;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        challengerTotal += Math.sqrt(nearestDistance(r, g, b, challenger));
        incumbentTotal += Math.sqrt(nearestDistance(r, g, b, incumbent));
        counted++;
      }
      if (!counted) return [0, 0];
      return [challengerTotal / counted, incumbentTotal / counted];
    }

    /*
      Rewrite every cell to its nearest slot.

      Returns nothing and accumulates nothing, which is the actual saving here.
      The old version measured the palette's fit as it wrote, so every cell paid
      for a Math.sqrt feeding a number the caller discarded. Scoring a palette
      and applying one are separate jobs because they run at different rates:
      applying is every frame, scoring is once every PALETTE_REFIT_MS.
    */
    function writePalette(data) {
      const slots = paletteFlat.length;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 16) continue;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        let slot = 0;
        let best = Infinity;
        for (let p = 0; p < slots; p += 3) {
          const d = colourDistance(r, g, b, paletteFlat[p], paletteFlat[p + 1], paletteFlat[p + 2]);
          if (d < best) {
            best = d;
            slot = p;
          }
        }
        data[i] = paletteFlat[slot];
        data[i + 1] = paletteFlat[slot + 1];
        data[i + 2] = paletteFlat[slot + 2];
      }
    }

    /* Pair each slot of the outgoing palette with its nearest colour in the
       incoming one, so a rebuild moves colours without swapping their places.
       The new colours are taken whole rather than blended in: a palette that
       chases every frame of a loop converges on the average of the loop, which
       is grey, instead of on any frame of it. */
    function matchPaletteSlots(previous, next) {
      if (!previous || previous.length !== next.length) return next;
      const unclaimed = next.slice();
      return previous.map((slot) => {
        let nearest = 0;
        let best = Infinity;
        for (let i = 0; i < unclaimed.length; i++) {
          const candidate = unclaimed[i];
          const distance = colourDistance(slot[0], slot[1], slot[2], candidate[0], candidate[1], candidate[2]);
          if (distance < best) {
            nearest = i;
            best = distance;
          }
        }
        return unclaimed.splice(nearest, 1)[0];
      });
    }

    function pixelate() {
      const [cols, rows] = gridFor(lerp(220, 26, amount));
      const refresh = toneDirty || work.width !== cols || work.height !== rows;
      if (refresh) sample(cols, rows);
      updateFaults(cols, rows);
      const pixels = refresh && (usePixelPalette || effectMotion) ? wctx.getImageData(0, 0, cols, rows) : null;
      const now = performance.now();
      /*
        The palette is held rather than recomputed, the way a GIF holds one
        global colour table for every frame of its animation. Rebuilding per
        frame is what turns an animation grey: the table chases a moving target
        and settles on the average of the loop instead of on any frame of it.

        What unseats it is a challenge rather than a guess. Every
        PALETTE_REFIT_MS the frame is offered a palette built from itself, and
        the incumbent stands down only if the challenger fits by
        PALETTE_REFIT_MARGIN. Over a sequence cutting between a photograph and
        colour bars, frames within a single scene put the challenger between
        0.94 and 1.43 times ahead while the two cuts put it 2.2 and 3.4 times
        ahead, so 1.6 sits in that gap with room on both sides. Comparing the
        two fits also survives a scene no six colours can cover, where both
        palettes score badly and any absolute threshold would rebuild forever.
      */
      if (pixels && usePixelPalette && (!pixelPalette || now - paletteProbedAt > PALETTE_REFIT_MS)) {
        paletteProbedAt = now;
        const challenger = extractPixelPalette(pixels.data, PIXEL_PALETTE_SIZE);
        if (challenger.length) {
          if (!pixelPalette) {
            setPixelPalette(challenger);
          } else {
            const [challengerFit, incumbentFit] = measureFits(pixels.data, challenger, pixelPalette);
            if (challengerFit * PALETTE_REFIT_MARGIN < incumbentFit) {
              setPixelPalette(matchPaletteSlots(pixelPalette, challenger));
            }
          }
        }
      }

      if (pixels && usePixelPalette && paletteFlat) {
        writePalette(pixels.data);
        wctx.putImageData(pixels, 0, 0);
      }
      if (pixels) pixelPixels = pixels.data;
      toneDirty = false;
      let output = work;

      // The grid stays put. Each excited cell borrows a directional neighbour,
      // charges, then restores. Read from the untouched sample so faults never
      // feed into one another or contaminate the next palette extraction.
      if (pixelPixels && faults.pulses.size) {
        if (!pixelResponseFrame || pixelResponseFrame.width !== cols || pixelResponseFrame.height !== rows) {
          plate.width = cols; plate.height = rows;
          pixelResponseFrame = pctx.createImageData(cols, rows);
        }
        pixelResponseFrame.data.set(pixelPixels);
        for (const [index, pulse] of faults.pulses) {
          const state = faults.states[index];
          if (!state) continue;
          const x = index % cols, y = Math.floor(index / cols);
          const bx = Math.max(0, Math.min(cols - 1, x + pulse.dx));
          const by = Math.max(0, Math.min(rows - 1, y + pulse.dy));
          const sourceIndex = (by * cols + bx) * 4;
          const ownIndex = index * 4;
          let color = Array.from(pixelPixels.subarray(sourceIndex, sourceIndex + 3));
          if (state === 1 || state === 3) {
            if (usePixelPalette && pixelPalette) {
              // Keep the six-colour promise, even in the brightest relay state.
              color = state === 3 ? pixelLight : pixelDark;
            } else {
              color = color.map(c => state === 3 ? lerp(c, 255, 0.58) : c * 0.32);
            }
          } else if (state === 2) {
            color = Array.from(pixelPixels.subarray(ownIndex, ownIndex + 3));
          }
          pixelResponseFrame.data.set(color, ownIndex);
        }
        pctx.putImageData(pixelResponseFrame, 0, 0);
        output = plate;
      }
      ctx.imageSmoothingEnabled = false;
      ctx.globalCompositeOperation = 'copy';
      ctx.drawImage(output, 0, 0, cols, rows, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
      faults.dirty = false;
    }

    function mosaic() {
      const cols = Math.max(1, Math.round(canvas.width / (mosaicSize * scale)));
      const rows = Math.max(1, Math.round(canvas.height / (mosaicSize * 1.5 * scale)));
      const cellW = canvas.width / cols, cellH = canvas.height / rows;
      // Compensate for rectangular samples so faces keep their proportions.
      const upload = sampleTone(cols, rows, cellH / cellW);
      const seam = Math.min(0.8, mosaicSize * 0.16) * scale;
      updateFaults(cols, rows);
      mosaicResponse = mosaicGlints(faults, effectTime, responseMotion, mosaicResponse);
      faults.dirty = false;
      if (gpu) {
        gpu.draw(work, { upload, cols, rows, amount, seam, glints: mosaicResponse });
        return;
      }

      // Canvas fallback shades only the small tile grid, then enlarges it.
      const pixels = wctx.getImageData(0, 0, cols, rows);
      const steps = Math.round(lerp(48, 8, amount));
      for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
        const i = (y * cols + x) * 4;
        const light = luma(pixels.data[i], pixels.data[i + 1], pixels.data[i + 2]) / 255;
        const variation = ((x * 17 + y * 131) % 101) / 100 - 0.5;
        for (let channel = 0; channel < 3; channel++) {
          let color = lerp(light, pixels.data[i + channel] / 255, 1 + 0.6 * amount);
          color = clamp01((color - 0.5) * (1 + 0.8 * amount) + 0.5);
          color = clamp01(color * (1 + variation * 0.18));
          pixels.data[i + channel] = Math.round(color * steps) / steps * 255;
        }
      }
      wctx.putImageData(pixels, 0, 0);
      blowUp(false);
      // Darken only the tile faces, preserving transparent source pixels.
      ctx.globalCompositeOperation = 'source-atop';
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = seam;
      ctx.beginPath();
      for (let x = 0; x <= cols; x++) {
        ctx.moveTo(x * cellW, 0); ctx.lineTo(x * cellW, canvas.height);
      }
      for (let y = 0; y <= rows; y++) {
        ctx.moveTo(0, y * cellH); ctx.lineTo(canvas.width, y * cellH);
      }
      ctx.stroke();
      // The fallback uses the same moving highlight, painted only over active
      // faces. Source-atop preserves transparent media and never shifts tiles.
      for (const [index] of faults.pulses) {
        const offset = index * 4;
        const charge = mosaicResponse[offset] / 255;
        if (!charge) continue;
        const peak = Math.max(pixels.data[offset], pixels.data[offset + 1], pixels.data[offset + 2]) / 255;
        const t = clamp01((peak - 0.015) / 0.485);
        const reflectance = t * t * (3 - 2 * t);
        if (!reflectance) continue;
        const age = mosaicResponse[offset + 1] / 255;
        let dx = mosaicResponse[offset + 2] / 255 * 2 - 1;
        let dy = mosaicResponse[offset + 3] / 255 * 2 - 1;
        const norm = Math.hypot(dx, dy);
        dx /= norm; dy /= norm;
        const gx = dx / cellW, gy = dy / cellH;
        const inverse = 1 / (gx * gx + gy * gy);
        const x = (index % cols) * cellW, y = Math.floor(index / cols) * cellH;
        const cx = x + cellW / 2, cy = y + cellH / 2;
        const gradient = ctx.createLinearGradient(cx - gx * inverse, cy - gy * inverse, cx + gx * inverse, cy + gy * inverse);
        for (let stop = 0; stop <= 16; stop++) {
          const position = stop / 16;
          const distance = ((position * 2 - 1) - lerp(-0.8, 0.8, age)) / 0.24;
          const band = Math.exp(-(distance * distance));
          const alpha = charge * reflectance * (0.08 + 0.32 * band);
          gradient.addColorStop(position, `rgba(255,255,255,${alpha})`);
        }
        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, cellW, cellH);
      }
      ctx.globalCompositeOperation = 'source-over';
    }

    /* Hard banding, no dither. Gradients become terraces, which is precisely
       the information a renderer has too much of. */
    function posterise() {
      const [cols, rows] = gridFor(Math.min(canvas.width, 1400));
      const levels = Math.max(2, Math.round(lerp(10, 2, amount)));
      const step = 255 / (levels - 1);
      if (gpu) { colorGpu(0, cols, rows, { steps: levels - 1 }); return; }
      prepareResponse();
      const charge = responseRaster(cols, rows);
      sample(cols, rows);
      const frame = wctx.getImageData(0, 0, cols, rows);
      const d = frame.data;
      if (!posterLut) {
        posterLut = new Uint8ClampedArray(256);
        for (let v = 0; v < 256; v++) posterLut[v] = Math.round(v / step) * step;
      }
      for (let i = 0; i < d.length; i += 4) {
        const shift = charge ? charge[i / 4] * 0.6 * step : 0;
        if (shift) {
          d[i] = clamp255(Math.round((d[i] + shift) / step) * step);
          d[i + 1] = clamp255(Math.round((d[i + 1] + shift) / step) * step);
          d[i + 2] = clamp255(Math.round((d[i + 2] + shift) / step) * step);
        } else {
          d[i] = posterLut[d[i]]; d[i + 1] = posterLut[d[i + 1]]; d[i + 2] = posterLut[d[i + 2]];
        }
      }
      wctx.putImageData(frame, 0, 0);
      blowUp(true);
    }

    /* Posterise with the threshold jittered per cell, so the terraces break
       into a cross-hatch instead of a hard edge. Deliberately run at a low
       buffer width: the lattice has to be visible to do anything. */
    function dither() {
      const [cols, rows] = gridFor(lerp(460, 110, amount));
      const levels = Math.max(2, Math.round(lerp(5, 2, amount)));
      const step = 255 / (levels - 1);
      if (gpu) { colorGpu(1, cols, rows, { steps: levels - 1 }); return; }
      prepareResponse();
      const charge = responseRaster(cols, rows);
      sample(cols, rows);
      const frame = wctx.getImageData(0, 0, cols, rows);
      const d = frame.data;
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const t = (((BAYER_8[(y & 7) * 8 + (x & 7)] + 0.5) / 64 - 0.5) + (charge ? charge[y * cols + x] * 0.6 : 0)) * step;
          const i = (y * cols + x) * 4;
          d[i] = clamp255(Math.round((d[i] + t) / step) * step);
          d[i + 1] = clamp255(Math.round((d[i + 1] + t) / step) * step);
          d[i + 2] = clamp255(Math.round((d[i + 2] + t) / step) * step);
        }
      }
      wctx.putImageData(frame, 0, 0);
      blowUp(false);
    }

    /*
      Gradient map. Luminance is replaced outright by a position on a two-ink
      ramp, so the original hue is discarded rather than tinted. That is the
      difference between this and the tone layer in CSS, and it is why this is
      the single strongest move against a synthetic palette: a generator's
      colour choices simply stop existing.
    */
    function duotone() {
      const [cols, rows] = gridFor(Math.min(canvas.width, 1600));
      const contrast = lerp(1, 2.4, amount);
      if (gpu) { colorGpu(2, cols, rows, { contrast }); return; }
      prepareResponse();
      const charge = responseRaster(cols, rows);
      if (!duotoneLut) {
        duotoneLut = new Uint8Array(768);
        for (let v = 0; v < 256; v++) {
          const t = clamp01((v / 255 - 0.5) * contrast + 0.5);
          duotoneLut[v * 3] = lerp(inkA[0], inkB[0], t);
          duotoneLut[v * 3 + 1] = lerp(inkA[1], inkB[1], t);
          duotoneLut[v * 3 + 2] = lerp(inkA[2], inkB[2], t);
        }
      }
      const lut = duotoneLut;
      sample(cols, rows);
      const frame = wctx.getImageData(0, 0, cols, rows);
      const d = frame.data;
      for (let i = 0; i < d.length; i += 4) {
        const l = (d[i] * 0.2126 + d[i + 1] * 0.7152 + d[i + 2] * 0.0722) | 0;
        if (charge && charge[i / 4]) {
          const tone = clamp01((l / 255 - 0.5) * contrast + 0.5 + charge[i / 4] * 0.2);
          d[i] = Math.floor(lerp(inkA[0], inkB[0], tone));
          d[i + 1] = Math.floor(lerp(inkA[1], inkB[1], tone));
          d[i + 2] = Math.floor(lerp(inkA[2], inkB[2], tone));
        } else {
          d[i] = lut[l * 3]; d[i + 1] = lut[l * 3 + 1]; d[i + 2] = lut[l * 3 + 2];
        }
      }
      wctx.putImageData(frame, 0, 0);
      blowUp(true);
    }

    /*
      Two-colour risograph.

      A duplicator cannot print a tone. It lays down solid ink or no ink, and
      every intermediate value is dot coverage — so each plate is screened to
      binary through the same Bayer matrix the dither mode uses, and that is
      what separates this from a gradient map with two colours.

      The second thing that separates it is the misregistration. Two plates
      that lined up exactly would composite to a duotone; the whole visual
      signature of the medium is that they do not, because the paper is fed
      through twice by hand. Both plates are also drawn slightly oversized, so
      the offset cannot expose a strip of bare paper at the frame edge.

      The plates are separated tonally rather than by hue. The first takes the
      whole range and carries the image; the second is a tent peaking in the
      midtones, so it neither duplicates the first nor fills the highlights,
      and the two only overprint where a real two-colour job would — which is
      where the third colour in a two-ink print comes from.
    */
    function riso() {
      /* A much finer screen than the dither mode runs, because a duplicator's
         ruling is fine enough to read as tone at arm's length and a coarse one
         reads as a checkerboard. */
      const [cols, rows] = gridFor(lerp(1000, 340, amount));
      /* Misregistration is a paper-feed error, so it is a fixed physical
         distance and does not scale with the screen ruling. */
      const drift = Math.max(2, Math.round(lerp(2, 10, amount)));
      if (gpu) { colorGpu(3, cols, rows, { drift }); return; }
      prepareResponse();
      const charge = responseRaster(cols, rows);
      const inks = [inkA, inkB];
      const offsets = [
        [-drift, -Math.round(drift * 0.4)],
        [Math.round(drift * 0.8), drift],
      ];

      sample(cols, rows);
      const src = wctx.getImageData(0, 0, cols, rows).data;

      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = `rgb(${paper[0]},${paper[1]},${paper[2]})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = false;
      /* Overprinting. Ink absorbs, so two plates darken each other rather than
         the later one hiding the earlier. Transparent plate pixels leave the
         paper untouched. */
      ctx.globalCompositeOperation = 'multiply';

      if (plate.width !== cols) plate.width = cols;
      if (plate.height !== rows) plate.height = rows;
      if (!risoFrames || risoFrames[0].width !== cols || risoFrames[0].height !== rows) {
        risoFrames = [pctx.createImageData(cols, rows), pctx.createImageData(cols, rows)];
      }
      const pad = drift * 2;

      for (let p = 0; p < 2; p++) {
        const ink = inks[p];
        const frame = risoFrames[p];
        const d = frame.data;
        d.fill(0);
        for (let y = 0; y < rows; y++) {
          for (let x = 0; x < cols; x++) {
            const i = (y * cols + x) * 4;
            const shift = p === 1 && charge ? charge[y * cols + x] * (drift + 2) : 0;
            const bx = shift ? Math.max(0, Math.min(cols - 1, Math.floor(x + 0.5 + shift * cols / (canvas.width + pad * 2)))) : x;
            const by = shift ? Math.max(0, Math.min(rows - 1, Math.floor(y + 0.5 - shift * 0.6 * rows / (canvas.height + pad * 2)))) : y;
            const sourceIndex = (by * cols + bx) * 4;
            const l = luma(src[sourceIndex], src[sourceIndex + 1], src[sourceIndex + 2]) / 255;
            /* Neither plate reaches full coverage, because paper showing
               through somewhere is most of why a print looks like a print. */
            const cov =
              p === 0
                ? (1 - l) * 0.9
                : (1 - Math.abs(l - 0.45) * 2.2) * 0.75;
            /* Half a cell of phase between the plates, standing in for the
               screen angle a real two-colour job would use. Without it both
               plates put their dots in the same place and the second is just
               overprinting the first. */
            const sx = p === 0 ? x : bx + 4;
            const sy = p === 0 ? y : by + 4;
            const threshold = (CLUSTER_8[(sy & 7) * 8 + (sx & 7)] + 0.5) / 64;
            if (cov <= threshold) continue;
            d[i] = ink[0];
            d[i + 1] = ink[1];
            d[i + 2] = ink[2];
            d[i + 3] = 255;
          }
        }
        pctx.putImageData(frame, 0, 0);
        ctx.drawImage(
          plate, 0, 0, cols, rows,
          offsets[p][0] - pad, offsets[p][1] - pad,
          canvas.width + pad * 2, canvas.height + pad * 2
        );
      }

      ctx.globalCompositeOperation = 'source-over';
    }

    /*
      Line screen. Tone becomes the thickness of a stroke instead of the size
      of a dot, which is the one structural axis the other modes leave open:
      dither disperses ink, riso clusters it, and neither of them draws a line.

      Coverage is thresholded against the distance to the nearest line centre
      along a rotated axis, so a stroke starts as a hairline and grows outward
      until it meets its neighbours.

      The second axis crosses the first but only engages in the lower half of
      the range, because an engraver adds a pass for the shadows and leaves the
      highlights on one. That white / stroke / weave / solid ladder is what
      stops a line screen reading as a barcode: with a single axis the darks
      have nowhere left to go once the strokes touch, and the whole bottom of
      the range collapses into flat ink.

      Both passes carry the same ink, and that is a decision rather than an
      omission. Giving the crossing pass the second ink was the first version
      of this, and it looked like a worse riso: a saturated ink at even a fifth
      coverage does not read as an accent in the shadows, it reads as a wash
      over the whole frame, because the eye integrates a fine screen into flat
      tone. One ink leaves the mode doing the one thing riso cannot, which is
      structure without hue.

      Polarity follows the paper, the same way the text mode's glyph ramp does.
      Light stock absorbs, so coverage tracks darkness — a print. Dark stock
      cannot absorb anything, so coverage tracks luminance and the strokes emit
      — a phosphor. One colour setting carries both conventions, which beats
      asking for a direction.
    */
    function engrave() {
      /* Tied to the canvas rather than to a constant, because the ruling below
         is a physical distance and has to be converted into this buffer's
         scale. Fine enough that the strokes survive the blow-up, small enough
         that the per-pixel pass stays inside the frame budget. */
      const cols0 = Math.min(canvas.width, Math.round(lerp(1500, 620, amount)));
      const [cols, rows] = gridFor(cols0);
      /* Screen ruling, in device pixels: an engraving's line spacing is a
         property of the plate, not of the shopper's display density. */
      const ruling = lerp(4, 12, amount) * scale;
      const pitch = Math.max(2.2, (ruling * cols) / canvas.width);
      /* Kept modest. A harder stretch pins most of a dark photograph to full
         coverage, and a screen with nothing left to give is a flat field. */
      const contrast = lerp(1, 1.35, amount);

      /* Normals, in radians, so the strokes themselves run at 65 and 135
         degrees. Both are kept well off the horizontal: a screen laid square
         to the frame beats against every horizon and every roof line in it. */
      const c0 = Math.cos(-0.44) / pitch;
      const s0 = Math.sin(-0.44) / pitch;
      const c1 = Math.cos(0.79) / pitch;
      const s1 = Math.sin(0.79) / pitch;

      const upload = sampleTone(cols, rows);
      const response = prepareResponse();

      /* Everything above is the plate and is shared, because the geometry *is*
         the mode; only the evaluation differs. */
      if (gpu) {
        gpu.draw(work, {
          upload, ...response, cols, rows, c0, s0, c1, s1, contrast,
          invert: paperIsLight,
          ink: glInk,
          paper: glPaper,
        });
        return;
      }

      const charge = responseRaster(cols, rows);
      const img = wctx.getImageData(0, 0, cols, rows);
      const d = img.data;

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const i = (y * cols + x) * 4;
          const l = luma(d[i], d[i + 1], d[i + 2]) / 255;
          const tone = paperIsLight ? 1 - l : l;
          const cov = clamp01((tone - 0.5) * contrast + 0.5);

          /* Distance to the nearest line centre, 0 at the centre and 1 midway
             between two, so it can be compared against coverage directly. */
          const value = charge ? charge[y * cols + x] : 0;
          const p0 = x * c0 + y * s0 + value * 0.28;
          const p1 = x * c1 + y * s1 - value * 0.18;
          const d0 = Math.abs(p0 - Math.floor(p0) - 0.5) * 2;
          const d1 = Math.abs(p1 - Math.floor(p1) - 0.5) * 2;

          /*
            The two caps are not symmetric, and the asymmetry is the whole
            shadow end of the picture. Given equal width both passes fuse at
            high coverage and what survives is the diamond where their two gaps
            intersect — an isolated speck of paper per cell, which is a dot
            screen with the tones swapped, not an engraving. So the first pass
            takes almost the full pitch and the second stays thin: the deepest
            shadow is then solid ink broken by a hairline of paper running
            along the strokes, which is what a burin actually leaves.

            It never quite closes, because paper showing through somewhere is
            most of why a print looks like a print.
          */
          const inked =
            d0 < cov * 0.95 || d1 < clamp01((cov - 0.5) / 0.5) * 0.35;
          const ink = inked ? inkA : paper;

          d[i] = ink[0];
          d[i + 1] = ink[1];
          d[i + 2] = ink[2];
          d[i + 3] = 255;
        }
      }

      wctx.putImageData(img, 0, 0);
      /* Smoothing on. A one-pixel stroke resampled with it off aliases into a
         moire that swamps the screen it came from, and a soft-edged line is
         what an inked one looks like anyway. */
      blowUp(true);
    }

    /*
      Text mode. Luminance picks the glyph and the cell's hue picks the ink,
      posterised to three levels per channel — 27 colours, close enough to an
      ANSI palette to read as a terminal.

      The two carry different halves of the image on purpose. Tone belongs
      entirely to the glyph, and the ink is normalised to full brightness
      before it is posterised, so a dark cell gets a dark *character* rather
      than a dark colour. Tinting the glyph with the cell's literal RGB is the
      intuitive version and it renders the shadows as black on black — which is
      also why a real terminal has sixteen fixed bright colours and spends
      density on tone instead.

      Every glyph is pre-drawn once per palette entry into an atlas, so the
      per-cell cost is a drawImage rather than a fillText. At a typical grid
      that is the difference between ~15,000 text layouts and ~270.
    */
    function buildAtlas(cellW, cellH) {
      const sheet = document.createElement('canvas');
      sheet.width = glyphs.length * cellW;
      sheet.height = 27 * cellH;
      const sctx = sheet.getContext('2d');
      sctx.font = `${cellH}px ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace`;
      sctx.textAlign = 'center';
      sctx.textBaseline = 'middle';
      for (let c = 0; c < 27; c++) {
        const r = ((c / 9) | 0) * 127;
        const g = (((c / 3) | 0) % 3) * 127;
        const b = (c % 3) * 127;
        sctx.fillStyle = `rgb(${r},${g},${b})`;
        for (let gi = 0; gi < glyphs.length; gi++) {
          sctx.fillText(glyphs[gi], gi * cellW + cellW / 2, c * cellH + cellH / 2);
        }
      }
      atlas = sheet;
      atlasCell = [cellW, cellH];
      if (gpu) gpu.uploadAtlas(sheet);
    }

    function ascii() {
      /* Cell size is a CSS measurement scaled up to device pixels, not a
         device measurement. Written the other way round — which it was — the
         grid comes out twice as dense on a 2x display as on a 1x one, so the
         same setting gives two different pictures and the retina one pays four
         times the cost for the privilege. */
      const cellH = Math.max(8, Math.round(lerp(9, 26, amount) * scale));
      const cellW = Math.max(5, Math.round(cellH * 0.6));
      if (!atlas || atlasCell[0] !== cellW || atlasCell[1] !== cellH) buildAtlas(cellW, cellH);

      const cols = Math.ceil(canvas.width / cellW);
      const rows = Math.ceil(canvas.height / cellH);
      const upload = sampleTone(cols, rows, cellH / cellW);
      const response = prepareResponse(cols, rows);
      if (gpu) {
        gpu.draw(work, {
          upload, ...response, cols, rows, cellW, cellH,
          glyphs: glyphs.length, atlasW: atlas.width, atlasH: atlas.height,
          invert: asciiPaperIsLight, paper: glAsciiPaper, floor: 0, span: 1,
        });
        return;
      }
      const charge = responseRaster(cols, rows);
      const d = wctx.getImageData(0, 0, cols, rows).data;

      /*
        Repaint only the cells whose glyph or ink changed since the last frame.
        Motion in a background rarely touches the whole frame, so on an
        animated source most cells are identical to what is already on the
        canvas and redrawing them is pure waste — this is the difference
        between text mode animating and text mode being too expensive to.

        A full clear is only correct on the first pass, or after the grid has
        been resized, because otherwise it would throw away the cells this pass
        is about to decide not to draw.
      */
      let prev = prevCells;
      if (!prev || prev.length !== cols * rows) {
        prev = softAscii ? new Float64Array(cols * rows).fill(-1) : new Int32Array(cols * rows).fill(-1);
        prevCells = prev;
        ctx.fillStyle = asciiPaperCss;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      /*
        Glyph density is ink coverage, so which way it maps onto tone depends
        entirely on what colour the ink is against. On black, a dense glyph
        emits light and tracks luminance directly. On white it absorbs light,
        so the same ramp read the same way turns the picture into its own
        negative — a printout has to run inverted.

        The ink brightness flips with it. Cell colours are normalised to the
        top of the 27-entry palette on a dark ground, which is what keeps a
        dim cell legible; on a light ground that same move puts neon on white
        and cannot be read at all, so the target drops to the middle level and
        the glyphs come out as dark saturated ink.
      */
      const inkTarget = asciiPaperIsLight ? 127 : 255;
      if (softAscii) {
        ctx.font = `${cellH}px ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      }

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const c = y * cols + x;
          const i = c * 4;
          const r = d[i], g = d[i + 1], b = d[i + 2];
          const l = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 255;
          let coverage = clamp01((asciiPaperIsLight ? 1 - l : l) + (charge ? charge[c] * 0.18 : 0));
          if (softAscii) coverage = clamp01((coverage - 0.08) / 0.92);
          const gi = Math.min(glyphs.length - 1, (coverage * glyphs.length) | 0);
          const k = inkTarget / (Math.max(r, g, b) || 1);
          const ci = gi === 0
            ? 0
            : (((r * k) / 96) | 0) * 9 + (((g * k) / 96) | 0) * 3 + (((b * k) / 96) | 0);

          const packed = softAscii ? gi * 16777216 + r * 65536 + g * 256 + b : (gi << 8) | ci;
          if (prev[c] === packed) continue;
          prev[c] = packed;

          ctx.fillStyle = asciiPaperCss;
          ctx.fillRect(x * cellW, y * cellH, cellW, cellH);
          if (gi === 0) continue;
          if (softAscii) {
            const inkScale = asciiPaperIsLight ? 0.45 : 1;
            ctx.fillStyle = `rgb(${r * inkScale},${g * inkScale},${b * inkScale})`;
            ctx.fillText(glyphs[gi], (x + 0.5) * cellW, (y + 0.5) * cellH);
            continue;
          }
          ctx.drawImage(
            atlas, gi * cellW, ci * cellH, cellW, cellH,
            x * cellW, y * cellH, cellW, cellH
          );
        }
      }
    }

    /*
      Glyph overlay. The same atlas as text mode, gated on local detail and
      composited over the photograph rather than in place of it.

      This is the only mode in the set that is not destructive, and that is the
      point of it. Every other pass throws the image away and hands the whole
      picture to whatever it puts back, which is why a quantised background
      cannot also be a photograph. Here the canvas keeps its alpha and most
      cells are cleared rather than painted, so the picture underneath survives
      and the glyphs are an annotation on it.

      Which changes what the glyphs are for. Text mode spends glyph density on
      tone because nothing else is left to carry it. Here the photograph
      already carries tone, so density is free to carry the thing tone cannot:
      local detail. A cell's glyph comes from the luminance *range* across its
      neighbours, so the field thickens over a cliff face or a cloud edge and
      vanishes across flat sky — which is what makes the effect read as
      something measuring the image instead of something laid on top of it.

      The ink is two-tone: white on a dark cell, black on a light one. Text
      mode's derivation is the wrong one here and looked it — normalising a
      cell's own colour up to the top of the 27-entry palette is what keeps a
      dim cell legible against a fixed paper, but over a photograph every cell
      has a different hue and the field comes out as neon confetti scattered
      across the picture. There is no quieter version available either, because
      the palette's channels step 0 / 127 / 254, so any faint tint quantises
      straight back to grey. Given the choice between loud colour and no
      colour, an annotation should be monochrome.
    */
    function readout() {
      /* Fixed, not driven by the amount. The glyph is a mark on a picture
         rather than the picture itself, so it wants to stay small — a coarse
         readout is just a worse text mode, and text mode is right there.
         Amount is left to do one job, which is coverage. */
      const cellH = Math.max(8, Math.round(9 * scale));
      const cellW = Math.max(5, Math.round(cellH * 0.6));
      if (!atlas || atlasCell[0] !== cellW || atlasCell[1] !== cellH) buildAtlas(cellW, cellH);

      const cols = Math.ceil(canvas.width / cellW);
      const rows = Math.ceil(canvas.height / cellH);
      const upload = sampleTone(cols, rows, cellH / cellW);
      const response = prepareResponse(cols, rows);

      /* How much local contrast earns a glyph. Amount lowers the bar, so it
         reads as the share of the frame that gets annotated rather than as a
         strength — at the bottom only the busiest few bands survive, at the
         top the field is close to continuous. */
      const floor = lerp(0.34, 0.03, amount);
      const span = 0.22;

      if (gpu) {
        gpu.draw(work, {
          upload, ...response, cols, rows, cellW, cellH, floor, span, paper: glAsciiPaper,
          glyphs: glyphs.length,
          atlasW: atlas.width,
          atlasH: atlas.height,
        });
        return;
      }

      const charge = responseRaster(cols, rows);
      const d = wctx.getImageData(0, 0, cols, rows).data;

      let prev = prevCells;
      if (!prev || prev.length !== cols * rows) {
        prev = softAscii ? new Float64Array(cols * rows).fill(-1) : new Int32Array(cols * rows).fill(-1);
        prevCells = prev;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      /* Luminance for the whole grid up front, because the detail pass reads
         each cell's neighbours and would otherwise recompute every one of them
         nine times over. */
      const lum = new Float32Array(cols * rows);
      for (let c = 0; c < lum.length; c++) {
        const i = c * 4;
        lum[c] = luma(d[i], d[i + 1], d[i + 2]) / 255;
      }

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const c = y * cols + x;

          let lo = 1;
          let hi = 0;
          const y0 = y > 0 ? y - 1 : 0;
          const y1 = y < rows - 1 ? y + 1 : rows - 1;
          const x0 = x > 0 ? x - 1 : 0;
          const x1 = x < cols - 1 ? x + 1 : cols - 1;
          for (let ny = y0; ny <= y1; ny++) {
            for (let nx = x0; nx <= x1; nx++) {
              const v = lum[ny * cols + nx];
              if (v < lo) lo = v;
              if (v > hi) hi = v;
            }
          }

          const range = hi - lo + (charge ? Math.abs(charge[c]) * 0.16 : 0);
          let packed = -1;
          let gi = 0;
          let ci = 0;

          if (range >= floor) {
            const t = clamp01((range - floor) / span);
            gi = Math.min(glyphs.length - 1, 1 + ((t * (glyphs.length - 1)) | 0));
            /* The palette's two ends, 0 and 26. Threshold above the midpoint
               rather than on it, because a glyph is thin and loses a contrast
               fight a solid block of the same colour would win. */
            ci = lum[c] > 0.62 ? 0 : 26;
            packed = (gi << 8) | ci;
          }

          if (prev[c] === packed) continue;
          prev[c] = packed;

          ctx.clearRect(x * cellW, y * cellH, cellW, cellH);
          if (packed === -1) continue;
          ctx.drawImage(
            atlas, gi * cellW, ci * cellH, cellW, cellH,
            x * cellW, y * cellH, cellW, cellH
          );
        }
      }
    }

    function halftone() {
      // Limit cell count on very large screens; spacing remains uniform on both axes.
      const pitch = Math.max(halftoneSize * scale, Math.sqrt(canvas.width * canvas.height / 42000));
      const cols = Math.max(1, Math.ceil(canvas.width / pitch));
      const rows = Math.max(1, Math.ceil(canvas.height / pitch));
      const refresh = toneDirty || work.width !== cols || work.height !== rows;
      if (refresh) sample(cols, rows);
      updateFaults(cols, rows, pitch);
      if (gpu) {
        gpu.draw(work, {
          upload: refresh, cols, rows, pitch, time: effectTime,
          motion: halftoneMotion, colors: halftoneColors,
          faults: faults.states, uploadFaults: faults.dirty,
          ink: unit(halftoneInkA), inkB: unit(halftoneInkB), paper: unit(halftonePaper),
        });
        faults.dirty = false;
        toneDirty = false;
        return;
      }

      // Same geometry on devices without WebGL; only the tiny tone grid is read back.
      if (refresh || !halftonePixels) halftonePixels = wctx.getImageData(0, 0, cols, rows).data;
      toneDirty = false;
      const data = halftonePixels;
      const smooth = (lo, hi, value) => {
        const t = clamp01((value - lo) / (hi - lo));
        return t * t * (3 - 2 * t);
      };
      const mix = (a, b, t) => a.map((c, i) => c + (b[i] - c) * t);
      ctx.fillStyle = `rgb(${halftonePaper.join(',')})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const seedAt = (x, y) => {
        let hash = (Math.imul(x, 374761393) + Math.imul(y, 668265263)) >>> 0;
        hash = Math.imul(hash ^ (hash >>> 13), 1274126177) >>> 0;
        return ((hash ^ (hash >>> 16)) >>> 0) / 4294967295;
      };
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const i = (y * cols + x) * 4;
          const rgba = [data[i], data[i + 1], data[i + 2], data[i + 3]];
          const rgb = rgba.slice(0, 3);
          const light = luma(...rgb) / 255;
          const contrast = Math.min(1, Math.hypot(...rgb.map((c, j) => (c - halftonePaper[j]) / 255)) / 1.15);
          const tone = halftoneColors === 1 ? contrast : contrast * 0.28 + light * 0.72;
          const seed = seedAt(x, y);
          const on = seed <= smooth(0.12, 0.95, tone) * 0.78 + 0.08 ? 1 : 0;
          const weak = seedAt(x + 53, y + 97) > 0.96 ? 0.45 : 1;
          const tripTick = Math.floor(effectTime * HALFTONE_FLICKER_HZ + seed * 19);
          const trip = seedAt(x + tripTick * 17 + 137, y + tripTick * 29 + 277);
          const level = on * weak * (trip < halftoneMotion * 0.003 ? 0.25 : 1);
          const state = faults.states[y * cols + x];
          const fault = state === 0 ? null : state === 1 ? 0 : state === 2 ? 0.4 : 1;
          const cx = (x + 0.5) * pitch, cy = (y + 0.5) * pitch;
          let color = rgb;
          if (halftoneColors === 0) {
            color = mix([74, 18, 235], [216, 69, 197], smooth(0.17, 0.42, light));
            const warm = smooth(0.05, 0.35, (rgb[0] - rgb[2]) / 255) * (1 - smooth(0.5, 0.8, light));
            color = mix(color, [235, 69, 59], warm * 0.9);
            color = mix(color, [153, 248, 249], smooth(0.42, 0.77, light));
          } else if (halftoneColors === 2) {
            color = mix(halftoneInkA, halftoneInkB, smooth(0.15, 0.85, light));
          }
          if (state === 3 || state === 4) color = mix(color, [255, 255, 255], state === 3 ? 0.78 : 0.28);
          ctx.fillStyle = `rgb(${color.map(Math.round).join(',')})`;
          ctx.globalAlpha = (fault !== null ? fault : level) * rgba[3] / 255;
          ctx.beginPath();
          ctx.arc(cx, cy, 0.4 * pitch, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    }

    const MODES = { mosaic, pixelate, posterise, dither, duotone, riso, ascii, 'ascii-soft': ascii, readout, engrave, halftone };
    const run = MODES[mode];
    if (!run) return;

    /*
      No mode is excluded by name. An animated source re-runs whichever pass it
      has, and the pass pays for itself out of a measured budget below — which
      is the same thing a blocklist was doing, except it also covers a slow
      phone, a 5K viewport, and any mode added later.

      Not const, because whether a source can be animated is a claim the markup
      makes from a file extension and only a decoder can settle. A provider that
      resolves to nothing revokes it, and the loop shuts down on one painted
      frame rather than spinning on a source that cannot advance.
    */
    let canLoop = animated || isVideo || isCanvas || effectMotion;

    /* Share of wall time the pass may occupy. A background is decoration, so
       it gets a small slice and yields the rest to the page. */
    const BUDGET = 0.15;
    /*
      A decoded animation gets no floor at all, because the provider marks a
      frame dirty only when one actually lands and the source's own timing
      therefore already paces this loop. A floor on top of that can only ever
      hold the effect *below* the source's rate, and that is precisely what the
      old constant did: 83ms against a GIF whose frames are 70ms apart dropped
      one frame in six, so the quantised copy slipped visibly behind the
      photograph it was made from.

      Video keeps a floor. requestVideoFrameCallback will happily deliver 60
      frames a second and a background does not need redrawing that often.
    */
    const MIN_INTERVAL = effectMotion ? 1000 / 30 : isVideo || isCanvas ? FRAME_MS : 0;
    const MAX_INTERVAL = 400;

    let raf = 0;
    let last = 0;
    let interval = MIN_INTERVAL;
    let nextPaint = 0;
    let averageCost = 0;

    /*
      Every mode that rewrites pixels — including limited-palette pixelate —
      has to get at the image's actual pixels, and an image served without CORS
      headers cannot supply them. Reading it back off a 2D canvas throws, and
      uploading it as a texture throws too.
      Shopify's CDN does send the headers, so this is the path for a source
      that is not Shopify's, and the answer is to abandon the effect rather
      than throw once a frame forever. Clearing is what makes that graceful:
      the canvas has an alpha channel, so an abandoned pass reveals the
      untouched photograph underneath instead of a hole.

      A lost GPU context arrives here by the same door. It is not worth
      recovering from on a decorative layer — see initQuantiseGpu.
    */
    let failed = false;

    /* Whether the layers beneath the canvas are still being held out of sight.
       Server-rendered, so this reads the attribute rather than assuming it. */
    let covering = root.hasAttribute('data-bg-quantise-covers');

    /* One exit for every way the pass can stop being possible: a source it
       cannot read, a GPU context taken away, a buffer the driver refuses. They
       all end the same way, because the background is the point and the effect
       is not. */
    function giveUp() {
      if (failed) return;
      failed = true;
      cursorEvents.abort();
      stop();
      if (provider) provider.destroy();
      provider = null;
      if (gpu) {
        if (!gpu.lost) { gpu.clear(); gpu.destroy(); }
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      /* Only still set if the pass never managed a single frame, since a
         successful one takes the cover off itself. Either way the photograph
         has to come back: there is now nothing else to show. */
      covering = false;
      root.removeAttribute('data-bg-quantise-covers');
      canvas.__bgQuantiseReady = false;
      if (canvas.hasAttribute('data-bg-frame-watch')) canvas.dispatchEvent(new Event('bg:frame'));
    }

    /*
      A lost context has to be answered when it is lost, not when something
      next tries to paint — because on a still background nothing ever does.
      There is no loop to notice, so the canvas would stay blank under hidden
      layers for the life of the page, which reads as a black screen and is the
      one outcome this whole layer exists to prevent.
      Registered after initQuantiseGpu's own listener, so `gpu.lost` is already
      true by the time this runs and no call is made into a dead context.
    */
    if (gpu) canvas.addEventListener('webglcontextlost', giveUp);

    function attempt() {
      if (failed) return false;
      try {
        if (gpu && gpu.lost) throw new Error('quantise: GPU context lost');
        run();
        /*
          The cover comes off the moment there is something to cover with, and
          it does not go back on.

          It was only ever there to hide the untreated photograph during the
          seconds before the first pass lands. Holding it for the life of the
          page instead makes the canvas the only thing standing between the
          viewer and the background colour, and every way a canvas can come
          back empty — a refit mid-drag, a context the OS took away, a buffer
          the driver would not back — then reads as a black screen. A
          destructive mode paints every pixel opaque, so once it has painted it
          occludes the photograph on its own and needs no help. Leaving the
          photograph in place underneath means an empty canvas costs a moment
          of untreated background instead of no background at all.
        */
        if (covering) {
          covering = false;
          root.removeAttribute('data-bg-quantise-covers');
        }
        canvas.__bgQuantiseReady = true;
        if (canvas.hasAttribute('data-bg-frame-watch')) canvas.dispatchEvent(new Event('bg:frame'));
        return true;
      } catch (e) {
        giveUp();
        return false;
      }
    }

    function paint() {
      if (failed || !frameW) return;
      fit();
      dirty = false;
      attempt();
    }

    function loop(now) {
      raf = requestAnimationFrame(loop);
      if (advance) advance();
      if (now + 0.5 < nextPaint) return;
      /* Nothing new to draw. The provider sets this when a frame lands, so a
         GIF holding one frame for a second costs one pass, not sixty. */
      const responding = effectMotion && (mode === 'halftone' || faults.pulses.size || faults.dirty);
      if ((!dirty && !responding) || !frameW) return;
      if (effectMotion) {
        const elapsed = Math.min(MAX_INTERVAL, last ? now - last : MIN_INTERVAL) / 1000;
        effectTime += elapsed;
      }
      last = now;
      dirty = false;

      const started = performance.now();
      if (!attempt()) return;
      const cost = performance.now() - started;
      // Keep fractional frame time: restarting a 24 Hz delay on each 60 Hz
      // tick rounds every interval up to 50 ms and silently renders at 20 Hz.
      averageCost = averageCost ? lerp(averageCost, cost, 0.2) : cost;
      interval = Math.min(MAX_INTERVAL, Math.max(MIN_INTERVAL, averageCost / BUDGET));
      nextPaint = (now - nextPaint > interval ? now : nextPaint) + interval;
    }

    /*
      The provider is started lazily and paused rather than destroyed, so a
      background that is merely hidden is not decoding frames nobody paints,
      and one that comes back does not re-fetch the file to do it.
    */
    function startProvider() {
      if (!animated && !isVideo && !isCanvas) return;
      if (providerWanted) {
        if (provider) provider.resume();
        return;
      }
      providerWanted = true;
      if (isVideo) {
        provider = driveVideo();
        return;
      }
      if (isCanvas) {
        provider = driveCanvas();
        return;
      }
      driveAnimatedImage().then((p) => {
        if (failed) { if (p) p.destroy(); return; }
        provider = p;
        if (!p) {
          /* Nothing can advance this source, so the loop has no work left to
             find. The frame already on the canvas is the whole effect. */
          canLoop = effectMotion;
          if (!canLoop) stop();
          return;
        }
        /* Resolved after a stop: honour the stop rather than the request that
           started the fetch. */
        if (!providerWanted) p.pause();
      });
    }

    function stopProvider() {
      providerWanted = false;
      if (provider) provider.pause();
    }

    /* Reduced motion freezes it on one frame rather than dropping the effect,
       which is the same thing the grain loop does. */
    function start() {
      if (!canLoop || reduced.matches || document.hidden || failed) return;
      startProvider();
      if (raf) return;
      last = 0;
      nextPaint = 0;
      raf = requestAnimationFrame(loop);
    }

    function stop() {
      stopProvider();
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    }

    /* Paint the element's own first frame straight away, then let a provider
       take over. A still image never gets one, and an animated source still
       shows something during the decode rather than a hole. */
    function begin() {
      const [w, h] = frameSize();
      if (!w) return;
      setFrame(source, w, h);
      paint();
      start();
    }

    if (isVideo) {
      if (source.readyState >= 2) begin();
      else source.addEventListener('loadeddata', begin, { once: true });
    } else if (isCanvas) {
      /* The renderer sizes its own canvas from its own deferred script, and in
         document order that has already happened. A second background higher up
         the page can pull this file in first though, and a canvas still at its
         default 300x150 quantises to a black rectangle — so wait a frame when
         the renderer has not mounted yet. */
      if (source.__vectorBg) begin();
      else requestAnimationFrame(begin);
    } else if (source.complete && source.naturalWidth > 0) {
      begin();
    } else {
      source.addEventListener('load', begin, { once: true });
    }

    /*
      Deliberately debounced rather than run per frame of a drag, and the delay
      is doing more work than it looks like it is.

      Refitting reallocates the backing store and hands it back empty, so a
      pass that refits on every resize event is a pass that can be caught
      mid-drag with nothing in it. Measured against a window whipped between
      500px and 2600px wide: refitting per frame left the canvas empty on
      inspection, while waiting for the drag to settle left it fully painted
      throughout. Waiting is not the lazy option here, it is the stable one —
      the layer is object-fit: cover, so the last frame drawn gets scaled and
      cropped to the new shape and stays up, visibly stretched, until the drag
      stops. That is the trade this wants: stumbling is fine, going dark is not.
    */
    // Static print can repaint after a font or graphic loads without changing
    // its dimensions. Notify the sampler as well as the live bloom plate.
    if (isCanvas) {
      source.setAttribute('data-bg-frame-watch', '');
      source.addEventListener('bg:frame', () => {
        if (failed) return;
        const [w, h] = frameSize();
        if (!w) return;
        setFrame(source, w, h);
        if (reduced.matches && !document.hidden) paint();
      }, { signal: cursorEvents.signal });
    }

    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(paint, 150);
    };
    const onVisibility = () => {
      if (document.hidden) { resetCursor(); stop(); }
      else start();
    };
    const onMotion = () => {
      resetCursor();
      if (reduced.matches) { stop(); paint(); }
      else start();
    };
    const onPageHide = () => {
      resetCursor();
      stop();
      if (provider) provider.destroy();
      provider = null;
    };
    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    reduced.addEventListener('change', onMotion);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onVisibility);
    canvas.__bgQuantiseCleanup = () => {
      giveUp();
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      reduced.removeEventListener('change', onMotion);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onVisibility);
      source.removeEventListener('load', begin);
      source.removeEventListener('loadeddata', begin);
      if (frame !== source && frame && typeof frame.close === 'function') frame.close();
    };
  }

  /*
    A cross-origin video only needs `crossorigin` so its frames can be read
    back, and setting it turns a missing CORS header from a lost effect into a
    video that will not play at all. Trade the effect away rather than the
    background: drop the attribute, reload, and let the quantise pass fall back
    to whatever it can still draw.
  */
  document.querySelectorAll('video[data-bg-cors-retry]').forEach((video) => {
    video.addEventListener(
      'error',
      () => {
        if (!video.crossOrigin) return;
        video.removeAttribute('crossorigin');
        video.removeAttribute('data-bg-cors-retry');
        video.load();
      },
      { once: true }
    );
  });

  document.querySelectorAll('canvas[data-bg-grain]').forEach((canvas) => {
    /* Same element and same slot in the stack as a grain stock, but nothing
       else in common: shape baked to a tile, colour painted every frame. */
    if (canvas.dataset.bgStock === 'sequin') startSequins(canvas);
    else startGrain(canvas);
  });
  document.querySelectorAll('canvas[data-bg-quantise]').forEach(startQuantise);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('canvas[data-bg-quantise]').forEach(startQuantise);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('canvas[data-bg-quantise]').forEach((canvas) => {
      if (canvas.__bgQuantiseCleanup) canvas.__bgQuantiseCleanup();
    });
  });
  document.querySelectorAll('video[data-bg-video]').forEach(gateVideo);
})();
